<template>
  <div class="ncrm-deal-details-page">
    <NcrmRecordLayout :left-lg-cols="3" :main-lg-cols-both="7" :right-lg-cols="2">
      <template #header>
        <NcrmRecordHeader
          :label="$t('ncrm.deals.details.recordLabel')"
          :title="recordTitle"
          :subtitle="recordSubtitle"
          :chips="headerChips"
          :kpis="headerKpis"
          :clickable-kpi-keys="['value']"
          show-back-button
          :back-aria-label="$t('app.actions.back')"
          @kpi-click="handleHeaderKpiClick"
          @back-click="goBack"
        >
          <template #kpis="{ kpis, isKpiClickable, onKpiClick }">
            <div class="ncrm-record-header__kpis">
              <template v-for="kpi in kpis" :key="`deal-kpi-${kpi.key}`">
                <v-menu
                  v-if="kpi.key === 'owner' && quickOwnerOptions.length"
                  :key="`deal-kpi-owner-menu-${kpi.key}`"
                  v-model="ownerMenuOpen"
                  location="bottom start"
                  :close-on-content-click="true"
                >
                  <template #activator="{ props: menuProps }">
                    <button
                      v-bind="menuProps"
                      type="button"
                      class="ncrm-record-header__kpi ncrm-record-header__kpi--clickable"
                      :disabled="ownerQuickActionLoading"
                      :aria-label="`${$t('ncrm.deals.details.fields.owner')}: ${ownerLabel(deal)}`"
                    >
                      <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                        <v-icon size="14">mdi-account</v-icon>
                        <span>{{ $t("ncrm.deals.details.fields.owner") }}</span>
                      </div>
                      <div class="d-flex align-center justify-space-between ga-2">
                        <div class="text-body-2 ncrm-record-header__kpi-value">
                          {{ ownerLabel(deal) }}
                        </div>
                        <v-icon size="16">mdi-menu-down</v-icon>
                      </div>
                    </button>
                  </template>
                  <v-list density="compact" class="ncrm-deal-details__kpi-menu-list">
                    <v-list-item
                      v-for="option in quickOwnerOptions"
                      :key="`deal-owner-option-${option.id}`"
                      :title="option.label"
                      :active="option.id === currentOwnerUserId"
                      :disabled="ownerQuickActionLoading || option.id === currentOwnerUserId"
                      @click="applyOwnerInline(option.id)"
                    >
                      <template #append v-if="option.id === currentOwnerUserId">
                        <v-icon size="16" color="primary">mdi-check</v-icon>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <div
                  v-else-if="kpi.key === 'owner'"
                  :key="`deal-kpi-owner-static-${kpi.key}`"
                  class="ncrm-record-header__kpi"
                >
                  <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                    <v-icon size="14">mdi-account</v-icon>
                    <span>{{ $t("ncrm.deals.details.fields.owner") }}</span>
                  </div>
                  <div class="text-body-2 ncrm-record-header__kpi-value">
                    {{ ownerLabel(deal) }}
                  </div>
                </div>
                <component
                  v-else
                  :is="isKpiClickable(kpi) ? 'button' : 'div'"
                  class="ncrm-record-header__kpi"
                  :class="{ 'ncrm-record-header__kpi--clickable': isKpiClickable(kpi) }"
                  :type="isKpiClickable(kpi) ? 'button' : undefined"
                  :aria-label="isKpiClickable(kpi) ? `${kpi.label}: ${kpi.value || '—'}` : undefined"
                  @click="onKpiClick(kpi)"
                >
                  <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                    <v-icon v-if="kpi.icon" size="14">{{ kpi.icon }}</v-icon>
                    <span>{{ kpi.label }}</span>
                  </div>
                  <div class="text-body-2 ncrm-record-header__kpi-value">
                    {{ kpi.value || "—" }}
                  </div>
                </component>
                <v-menu
                  v-if="kpi.key === 'activities' && stageOptions.length"
                  :key="`deal-kpi-stage-menu-${kpi.key}`"
                  location="bottom start"
                  :close-on-content-click="true"
                >
                  <template #activator="{ props: menuProps }">
                    <button
                      v-bind="menuProps"
                      type="button"
                      class="ncrm-record-header__kpi ncrm-record-header__kpi--clickable"
                      :disabled="!deal || inlineStageLoading"
                      :aria-label="`${$t('ncrm.deals.details.actions.changeStage')}: ${currentStageLabel}`"
                    >
                      <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                        <v-icon size="14">mdi-flag-outline</v-icon>
                        <span>{{ $t("ncrm.deals.details.actions.changeStage") }}</span>
                      </div>
                      <div class="d-flex align-center justify-space-between ga-2">
                        <div class="text-body-2 ncrm-record-header__kpi-value">
                          {{ currentStageLabel }}
                        </div>
                        <v-icon size="16">mdi-menu-down</v-icon>
                      </div>
                    </button>
                  </template>
                  <v-list density="compact" class="ncrm-deal-details__kpi-menu-list">
                    <v-list-item
                      v-for="option in stageOptions"
                      :key="`deal-stage-option-${option.id}`"
                      :title="option.label"
                      :active="option.id === currentStageId"
                      :disabled="inlineStageLoading || option.id === currentStageId"
                      @click="applyStageInline(option.id)"
                    >
                      <template #append v-if="option.id === currentStageId">
                        <v-icon size="16" color="primary">mdi-check</v-icon>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <div
                  v-else-if="kpi.key === 'activities'"
                  :key="`deal-kpi-stage-static-${kpi.key}`"
                  class="ncrm-record-header__kpi"
                >
                  <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                    <v-icon size="14">mdi-flag-outline</v-icon>
                    <span>{{ $t("ncrm.deals.details.actions.changeStage") }}</span>
                  </div>
                  <div class="text-body-2 ncrm-record-header__kpi-value">
                    {{ currentStageLabel }}
                  </div>
                </div>
              </template>
            </div>
          </template>
          <template #actions>
            <div class="ncrm-deal-details__header-actions">
              <v-btn
                size="small"
                variant="elevated"
                color="primary"
                :disabled="!deal"
                @click="openAddActivity"
              >
                <v-icon start>mdi-plus</v-icon>
                {{ $t("ncrm.activity.actions.add") }}
              </v-btn>
              <div class="ncrm-deal-details__header-actions-secondary">
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  :loading="loading"
                  @click="loadDeal"
                >
                  <v-icon start>mdi-refresh</v-icon>
                  {{ $t("ncrm.deals.actions.refresh") }}
                </v-btn>
                <v-btn
                  size="small"
                  variant="outlined"
                  color="primary"
                  :disabled="!deal"
                  @click="openEditDialog"
                >
                  <v-icon start>mdi-pencil</v-icon>
                  {{ $t("app.actions.edit") }}
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  color="error"
                  :disabled="!deal"
                  @click="deleteDialog = true"
                >
                  <v-icon start>mdi-delete</v-icon>
                  {{ $t("app.actions.delete") }}
                </v-btn>
              </div>
            </div>
          </template>
        </NcrmRecordHeader>
      </template>
      <template #left>
        <div class="ncrm-deal-details__side-column">
          <NcrmRecordPropertiesCard
            :title="$t('ncrm.contacts.details.sections.companyProperties')"
            :loading="loading || relatedPropertiesLoading"
            :sections="companyPropertySectionsForDisplay"
            :skeleton-groups="relatedPropertySkeletonGroups"
            :empty-label="emptyPropertyValueLabel"
            :empty-sections-label="$t('ncrm.contacts.details.propertySections.empty')"
            :copy-aria-label="$t('ncrm.contacts.details.propertyInteractions.copy')"
            :resolve-row-link="resolveRelatedPropertyRowLink"
            :privacy-subject="{ type: 'company', id: deal?.ncrm_company_id }"
            :resolve-row-tooltip="resolveRelatedTimestampTooltip"
            :can-copy-row="canCopyRelatedPropertyRow"
            show-settings-button
            :settings-disabled="!deal || !deal.ncrm_company_id || !companyPropertyLayoutEndpoint"
            :settings-aria-label="$t('ncrm.contacts.details.propertyLayout.actions.open')"
            @settings-click="openPropertyLayoutDrawer('company')"
            @copy-row="copyRelatedPropertyRowValue"
          />
          <NcrmRecordPropertiesCard
            :title="$t('ncrm.contacts.details.sections.personProperties')"
            :loading="loading || relatedPropertiesLoading"
            :sections="personPropertySectionsForDisplay"
            :skeleton-groups="relatedPropertySkeletonGroups"
            :empty-label="emptyPropertyValueLabel"
            :empty-sections-label="$t('ncrm.contacts.details.propertySections.empty')"
            :copy-aria-label="$t('ncrm.contacts.details.propertyInteractions.copy')"
            :resolve-row-link="resolveRelatedPropertyRowLink"
            :privacy-subject="{ type: 'person', id: deal?.ncrm_person_id }"
            :resolve-row-tooltip="resolveRelatedTimestampTooltip"
            :can-copy-row="canCopyRelatedPropertyRow"
            show-settings-button
            :settings-disabled="!deal || !deal.ncrm_person_id || !personPropertyLayoutEndpoint"
            :settings-aria-label="$t('ncrm.contacts.details.propertyLayout.actions.open')"
            @settings-click="openPropertyLayoutDrawer('person')"
            @copy-row="copyRelatedPropertyRowValue"
          />
        </div>
      </template>
      <template #main>
        <v-card class="pa-4">
          <div class="text-subtitle-1 ncrm-record-title">{{ $t("ncrm.deals.details.sections.mainWorkspace") }}</div>
          <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
          <div v-else class="mt-3">
            <v-tabs v-model="mainTab" color="primary" density="comfortable" show-arrows class="ncrm-main-workspace-tabs">
              <v-tab value="activity">{{ $t("ncrm.deals.details.tabs.activity") }}</v-tab>
              <v-tab value="products">{{ $t("ncrm.deals.details.tabs.products") }}</v-tab>
              <v-tab value="overview">{{ $t("ncrm.deals.details.tabs.overview") }}</v-tab>
              <v-tab value="mailjet">{{ $t("ncrm.integrations.mailjet.activity.tab") }}</v-tab>
              <v-tab value="history">{{ $t("ncrm.deals.details.tabs.history") }}</v-tab>
            </v-tabs>
            <v-window v-model="mainTab" class="ncrm-main-workspace-window" :touch="false">
              <v-window-item value="activity">
                <NcrmActivityTimeline
                  v-if="mainTab === 'activity' && dealId"
                  ref="dealActivityTimeline"
                  entity-type="deal"
                  :entity-id="dealId"
                  scope="customer"
                  :composer-open="activityComposerOpen"
                  sort-mode="active_overdue_first"
                  @update:composer-open="onDealActivityComposerToggle"
                  @created="onDealActivityCreated"
                  @changed="onDealActivityChanged"
                />
              </v-window-item>
              <v-window-item value="products">
                <div ref="lineItemsSection" class="ncrm-deal-details__products-panel">
                  <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-3">
                    <div>
                      <div class="text-subtitle-2 ncrm-text-strong">
                        {{ $t("ncrm.deals.details.lineItems.title") }}
                      </div>
                      <div class="text-body-2 text-medium-emphasis">
                        {{ $t("ncrm.deals.details.lineItems.subtitle") }}
                      </div>
                    </div>
                    <div class="d-flex flex-wrap align-center ga-2">
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        :loading="lineItemsLoading"
                        @click="fetchLineItems"
                      >
                        <v-icon start>mdi-refresh</v-icon>
                        {{ $t("ncrm.deals.actions.refresh") }}
                      </v-btn>
                      <v-btn size="small" color="primary" @click="openCreateLineItemDialog">
                        <v-icon start>mdi-plus</v-icon>
                        {{ $t("ncrm.deals.details.lineItems.actions.add") }}
                      </v-btn>
                    </div>
                  </div>
                  <div class="d-flex flex-wrap align-center justify-end ga-2 mb-4">
                    <v-chip variant="outlined">
                      {{ $t("ncrm.deals.details.lineItems.summary.count", { count: formatNumber(lineItemsSummary.count) }) }}
                    </v-chip>
                    <v-chip variant="outlined">
                      {{ $t("ncrm.deals.details.lineItems.summary.subtotal", { value: formatMoney(lineItemsSummary.subtotal, lineItemsCurrency) }) }}
                    </v-chip>
                    <v-chip color="primary">
                      {{ $t("ncrm.deals.details.lineItems.summary.total", { value: formatMoney(lineItemsSummary.total, lineItemsCurrency) }) }}
                    </v-chip>
                  </div>
                  <div v-if="lineItemsLoading" class="ncrm-deal-details__products-loading">
                    <v-skeleton-loader type="article" />
                    <v-skeleton-loader type="article" />
                  </div>
                  <v-alert v-else-if="!lineItems.length" type="info">
                    {{ $t("ncrm.deals.details.lineItems.empty") }}
                  </v-alert>
                  <div v-else class="ncrm-deal-details__products-grid">
                    <v-card
                      v-for="item in lineItems"
                      :key="`deal-line-item-${item.id}`"
                      variant="outlined"
                      class="ncrm-deal-details__product-card"
                    >
                      <div class="ncrm-deal-details__product-card-header">
                        <div class="ncrm-deal-details__product-card-title-block">
                          <div class="text-body-1 ncrm-text-strong">
                            {{ item.name_snapshot || `#${item.id}` }}
                          </div>
                          <div v-if="item.product_sku" class="text-caption text-medium-emphasis">
                            {{ item.product_sku }}
                          </div>
                        </div>
                        <div class="ncrm-deal-details__product-card-header-side">
                          <v-chip color="primary">
                            {{ lineItemProductTypeLabel(item.product_type_snapshot) }}
                          </v-chip>
                          <div class="d-flex flex-wrap justify-end ga-1">
                            <v-btn size="small" variant="text" color="primary" @click="openEditLineItemDialog(item)">
                              <v-icon start>mdi-pencil</v-icon>
                              {{ $t("app.actions.edit") }}
                            </v-btn>
                            <v-btn size="small" variant="text" color="error" @click="openDeleteLineItemDialog(item)">
                              <v-icon start>mdi-delete</v-icon>
                              {{ $t("app.actions.delete") }}
                            </v-btn>
                          </div>
                        </div>
                      </div>
                      <div class="ncrm-deal-details__product-card-metrics">
                        <div class="ncrm-deal-details__product-card-metric">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.lineItems.headers.quantity") }}
                          </div>
                          <div class="text-body-2 ncrm-text-strong">
                            {{ formatQuantity(item.quantity) }}
                          </div>
                        </div>
                        <div class="ncrm-deal-details__product-card-metric">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.lineItems.headers.unitPrice") }}
                          </div>
                          <div class="text-body-2 ncrm-text-strong">
                            {{ formatMoney(item.unit_price, item.currency) }}
                          </div>
                        </div>
                        <div class="ncrm-deal-details__product-card-metric">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.lineItems.headers.discount") }}
                          </div>
                          <div class="text-body-2 ncrm-text-strong">
                            {{ lineItemDiscountLabel(item) }}
                          </div>
                        </div>
                        <div class="ncrm-deal-details__product-card-metric">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.lineItems.headers.total") }}
                          </div>
                          <div class="text-body-2 ncrm-text-strong">
                            {{ formatMoney(item.total_amount, item.currency) }}
                          </div>
                        </div>
                      </div>
                      <div class="ncrm-deal-details__product-card-details">
                        <div class="ncrm-deal-details__product-card-detail">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.lineItems.headers.billing") }}
                          </div>
                          <div class="text-body-2">
                            {{ billingPeriodLabel(item.billing_period_snapshot, item.product_type_snapshot) }}
                          </div>
                        </div>
                        <div class="ncrm-deal-details__product-card-detail">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.lineItems.headers.subscription") }}
                          </div>
                          <div class="text-body-2">
                            {{ lineItemSubscriptionLabel(item) }}
                          </div>
                        </div>
                        <div v-if="item.notes" class="ncrm-deal-details__product-card-detail ncrm-deal-details__product-card-detail--full">
                          <div class="text-caption text-medium-emphasis">
                            {{ $t("ncrm.deals.details.fields.notes") }}
                          </div>
                          <div class="text-body-2 ncrm-deal-details__product-card-notes">
                            {{ item.notes }}
                          </div>
                        </div>
                      </div>
                    </v-card>
                  </div>
                </div>
              </v-window-item>
              <v-window-item value="overview">
                <div class="ncrm-deal-details__overview">
                  <div v-if="overviewRows.length" class="ncrm-details-list mt-1">
                    <div
                      v-for="row in overviewRows"
                      :key="`deal-overview-${row.key}`"
                      class="ncrm-details-list__item"
                    >
                      <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
                      <div class="text-body-2 ncrm-details-list__value">{{ row.value || "-" }}</div>
                    </div>
                  </div>
                  <v-alert v-else type="info">
                    {{ $t("ncrm.deals.details.overview.empty") }}
                  </v-alert>
                </div>
              </v-window-item>
              <v-window-item value="mailjet">
                <NcrmMailjetActivityPanel
                  v-if="mainTab === 'mailjet' && dealId"
                  entity-type="deal"
                  :entity-id="dealId"
                  @notify="onMailjetNotify"
                />
              </v-window-item>
              <v-window-item value="history">
                <NcrmRecordHistoryTimeline
                  v-if="mainTab === 'history' && dealId"
                  entity-type="deal"
                  :entity-id="dealId"
                />
              </v-window-item>
            </v-window>
          </div>
        </v-card>
      </template>
      <template #right>
        <v-card class="pa-4">
          <div class="text-subtitle-1 ncrm-record-title">{{ $t("ncrm.deals.details.sections.context") }}</div>
          <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-3" />
          <div v-else class="mt-3">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.deals.details.sections.sourceNavigation") }}</div>
            <div class="d-flex flex-wrap ga-2 mt-2">
              <v-btn
                v-if="sourceCompanyRoute"
                size="small"
                variant="outlined"
                color="primary"
                @click="openSourceCompany"
              >
                <v-icon start>mdi-domain</v-icon>
                {{ $t("ncrm.deals.details.actions.openCompany") }}
              </v-btn>
              <v-btn
                v-if="sourcePersonRoute"
                size="small"
                variant="outlined"
                color="primary"
                @click="openSourcePerson"
              >
                <v-icon start>mdi-account</v-icon>
                {{ $t("ncrm.deals.details.actions.openPerson") }}
              </v-btn>
              <v-btn
                v-if="sourceLeadRoute"
                size="small"
                variant="outlined"
                color="primary"
                @click="openSourceLead"
              >
                <v-icon start>mdi-open-in-new</v-icon>
                {{ $t("ncrm.deals.details.actions.openSourceLead") }}
              </v-btn>
            </div>
            <v-alert v-if="isDealCreatedManually" type="info" density="comfortable" class="mt-3">
              {{ $t("ncrm.deals.details.context.manualCreationMessage") }}
            </v-alert>
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.deals.details.sections.properties") }}</div>
            <div class="ncrm-details-list mt-2">
              <div
                v-for="row in dealPropertyRows"
                :key="`deal-context-property-${row.key}`"
                class="ncrm-details-list__item"
              >
                <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
                <div class="text-body-2 ncrm-details-list__value">{{ row.value }}</div>
              </div>
            </div>
            <div class="text-caption text-medium-emphasis mt-3">{{ $t("ncrm.deals.details.sections.context") }}</div>
            <div class="ncrm-details-list mt-2">
              <div
                v-for="row in dealContextRows"
                :key="`deal-context-row-${row.key}`"
                class="ncrm-details-list__item"
              >
                <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
                <div class="text-body-2 ncrm-details-list__value">{{ row.value }}</div>
              </div>
            </div>
          </div>
        </v-card>
      </template>
    </NcrmRecordLayout>
    <NcrmRecordEditDialog
      v-model="editDialog"
      :title="$t('ncrm.deals.details.edit.title')"
      :loading="editLoading"
      :save-disabled="editLoading"
      :max-width="820"
      @save="submitEdit"
    >
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="editForm.name"
            :label="$t('ncrm.deals.details.edit.fields.name')"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="editForm.owner_user_id"
            :items="ownerOptions"
            item-title="label"
            item-value="id"
            :label="$t('ncrm.deals.details.edit.fields.owner')"
            clearable
            :loading="ownerLoading"
            :no-filter="true"
            :menu-props="selectMenuProps"
            @focus="ensureOwnerOptionsLoaded"
            @update:search="handleOwnerSearch"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="editForm.stage_id"
            :items="stageOptions"
            item-title="label"
            item-value="id"
            :label="$t('ncrm.deals.details.edit.fields.stage')"
            :menu-props="selectMenuProps"
            @update:model-value="handleEditStageChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="editForm.status"
            :items="statusOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.deals.details.edit.fields.status')"
            :menu-props="selectMenuProps"
            @update:model-value="handleEditStatusChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="editForm.product_ids"
            :items="editProductSelectionOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.deals.details.edit.fields.products')"
            multiple
            chips
            closable-chips
            clearable
            :loading="editProductsLoading"
            :no-filter="true"
            :menu-props="selectMenuProps"
            @focus="ensureEditProductOptionsLoaded"
            @update:search="handleEditProductSearch"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="editForm.expected_close_date"
            :label="$t('ncrm.deals.details.edit.fields.expectedCloseDate')"
            type="date"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="editForm.ncrm_company_id"
            :items="editCompanyOptions"
            item-title="label"
            item-value="id"
            :label="$t('ncrm.deals.details.edit.fields.company')"
            clearable
            :loading="editCompaniesLoading"
            :no-filter="true"
            :menu-props="selectMenuProps"
            @update:search="handleEditCompanySearch"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="editForm.ncrm_person_id"
            :items="editPersonOptions"
            item-title="label"
            item-value="id"
            :label="$t('ncrm.deals.details.edit.fields.person')"
            clearable
            :loading="editPeopleLoading"
            :no-filter="true"
            :menu-props="selectMenuProps"
            @update:search="handleEditPersonSearch"
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="editForm.notes"
            :label="$t('ncrm.deals.details.edit.fields.notes')"
            rows="2"
            auto-grow
          />
        </v-col>
      </v-row>
    </NcrmRecordEditDialog>
    <NcrmDealWonHandoffDialog
      v-model="wonHandoffDialog"
      :deal-id="dealId"
      :deal-name="deal?.name || ''"
      :target-stage-id="pendingWonHandoffRequest?.targetStageId"
      :deal-patch="pendingWonHandoffRequest?.dealPatch || null"
      :initial-line-item-ids="pendingWonHandoffRequest?.initialLineItemIds ?? null"
      :initial-product-ids-to-attach="pendingWonHandoffRequest?.initialProductIdsToAttach || []"
      :select-menu-props="selectMenuProps"
      @success="handleWonHandoffSuccess"
      @cancelled="handleWonHandoffCancelled"
      @error="handleWonHandoffError"
    />
    <NcrmDealLostReasonDialog
      v-model="lostReasonDialog"
      :deal-name="deal?.name || ''"
      :target-status-label="lostReasonDialogStatusLabel"
      :loading="lostReasonLoading"
      @confirm="handleLostReasonConfirm"
      @cancelled="handleLostReasonCancelled"
    />
    <v-dialog v-model="deleteDialog" max-width="620">
      <v-card>
        <v-card-title>{{ $t("ncrm.deals.details.delete.title") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <div class="text-body-2 mb-3">{{ $t("ncrm.deals.details.delete.hint") }}</div>
          <v-text-field
            v-model="deleteForm.confirmText"
            :label="$t('ncrm.deals.details.delete.confirmLabel')"
          />
          <v-textarea
            v-model="deleteForm.deleteReason"
            :label="$t('ncrm.deals.details.delete.reasonLabel')"
            rows="2"
            auto-grow
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="deleteDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn
            color="error"
            :loading="deleteLoading"
            :disabled="deleteLoading || !canDeleteSubmit"
            @click="submitDelete"
          >
            {{ $t("app.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <NcrmRecordEditDialog
      v-model="lineItemDialog"
      :title="
        editingLineItemId
          ? $t('ncrm.deals.details.lineItems.dialog.editTitle')
          : $t('ncrm.deals.details.lineItems.dialog.addTitle')
      "
      :loading="lineItemSaving"
      :save-disabled="lineItemSaving || !canSubmitLineItem"
      :save-label="editingLineItemId ? $t('app.actions.save') : $t('app.actions.create')"
      :max-width="860"
      @save="submitLineItem"
    >
      <v-row dense>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="lineItemForm.product_id"
            :items="productOptions"
            item-title="label"
            item-value="id"
            :label="$t('ncrm.deals.details.lineItems.fields.product')"
            clearable
            :loading="productsLoading"
            :no-filter="true"
            :menu-props="selectMenuProps"
            @focus="ensureProductsCatalogLoaded"
            @update:search="handleLineItemProductSearch"
            @update:model-value="handleLineItemProductChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="lineItemForm.name"
            :label="$t('ncrm.deals.details.lineItems.fields.name')"
            required
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="lineItemForm.product_type"
            :items="lineItemProductTypeOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.deals.details.lineItems.fields.type')"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="lineItemForm.quantity"
            :label="$t('ncrm.deals.details.lineItems.fields.quantity')"
            type="number"
            min="0.001"
            step="0.001"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="lineItemForm.unit_price"
            :label="$t('ncrm.deals.details.lineItems.fields.unitPrice')"
            type="number"
            min="0"
            step="0.01"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="lineItemForm.discount_type"
            :items="discountTypeOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.deals.details.lineItems.fields.discountType')"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="lineItemForm.discount_value"
            :label="$t('ncrm.deals.details.lineItems.fields.discountValue')"
            type="number"
            min="0"
            step="0.01"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="lineItemForm.currency"
            :label="$t('ncrm.deals.details.lineItems.fields.currency')"
            maxlength="8"
          />
        </v-col>
        <v-col cols="12" md="6" v-if="lineItemForm.product_type === 'subscription'">
          <v-select
            v-model="lineItemForm.billing_period"
            :items="billingPeriodOptions"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.deals.details.lineItems.fields.billingPeriod')"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col cols="12" md="6" v-if="lineItemForm.product_type === 'subscription'" class="d-flex align-center">
          <v-switch
            v-model="lineItemForm.is_active_subscription"
            inset
            color="primary"
            :label="$t('ncrm.deals.details.lineItems.fields.subscriptionActive')"
            hide-details
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="lineItemForm.notes"
            :label="$t('ncrm.deals.details.lineItems.fields.notes')"
            rows="2"
            auto-grow
          />
        </v-col>
      </v-row>
    </NcrmRecordEditDialog>
    <v-dialog v-model="lineItemDeleteDialog" max-width="560">
      <v-card>
        <v-card-title>{{ $t("ncrm.deals.details.lineItems.delete.title") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <div class="text-body-2">
            {{
              $t("ncrm.deals.details.lineItems.delete.hint", {
                name: selectedLineItemForDelete?.name_snapshot || `#${selectedLineItemForDelete?.id || ""}`,
              })
            }}
          </div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="lineItemDeleteDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="error" :loading="lineItemDeleteLoading" @click="confirmDeleteLineItem">
            {{ $t("app.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <NcrmPropertyLayoutDrawer
      v-model="propertyLayoutDrawer"
      :title="$t('ncrm.contacts.details.propertyLayout.title')"
      :subtitle="$t('ncrm.contacts.details.propertyLayout.subtitle')"
      :items="activePropertyLayoutItems"
      :visible-keys="activePropertyLayoutVisibleKeys"
      :loading="activePropertyLayoutLoading"
      :saving="activePropertyLayoutSaving"
      @save="savePropertyLayout"
    />
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>
<script>
import axios from "axios";
import NcrmActivityTimeline from "@/components/ncrm/shared/NcrmActivityTimeline.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordHeader from "@/components/ncrm/shared/NcrmRecordHeader.vue";
import NcrmRecordHistoryTimeline from "@/components/ncrm/shared/NcrmRecordHistoryTimeline.vue";
import NcrmMailjetActivityPanel from "@/components/ncrm/integrations/mailjet/NcrmMailjetActivityPanel.vue";
import NcrmRecordLayout from "@/components/ncrm/shared/NcrmRecordLayout.vue";
import NcrmPropertyLayoutDrawer from "@/components/ncrm/shared/NcrmPropertyLayoutDrawer.vue";
import NcrmRecordPropertiesCard from "@/components/ncrm/shared/NcrmRecordPropertiesCard.vue";
import NcrmDealLostReasonDialog from "@/components/ncrm/deals/NcrmDealLostReasonDialog.vue";
import NcrmDealWonHandoffDialog from "@/components/ncrm/deals/NcrmDealWonHandoffDialog.vue";
import { formatNcrmTimestampTooltip, resolveNcrmPropertyLinkMeta } from "@/Utile/ncrmRecordDetails.mjs";
import {
  buildNcrmPropertyLayoutEndpoint,
  buildNcrmPropertyRowMap,
  dedupeNcrmPropertyLayoutKeys,
  resolveNcrmOrderedPropertyRows,
  resolveNcrmPropertyLayoutItems,
  resolveNcrmPropertyLayoutState,
  sanitizeNcrmPropertyLayoutVisibleKeys,
} from "@/Utile/ncrmPropertyLayouts.mjs";
import { dealDetailsActivityComputed, dealDetailsActivityMethods } from "@/Utile/ncrmDealDetailsActivity.mjs";
import { dealLostReasonMethods } from "@/Utile/ncrmDealLostReason.mjs";
import { formatDisplayAmount, formatDisplayNumber, formatDisplayDate, formatDisplayDateTime } from "@/Utile/displayFormat.mjs";
const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const normalizeDate = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};
const DEAL_DETAILS_TABS = ["activity", "products", "overview", "history", "mailjet"];
const RELATED_PROPERTY_SECTION_ORDER = ["contact", "address", "business", "customFields", "system"];
const RELATED_PROPERTY_SKELETON_GROUPS = [2, 2, 2];
const DEAL_COMPANY_PROPERTY_LAYOUT_SLOT = "deal_company_sidebar";
const DEAL_PERSON_PROPERTY_LAYOUT_SLOT = "deal_person_sidebar";
const EDIT_DEAL_SELECTION_KINDS = Object.freeze({
  LINE_ITEM: "line-item",
  PRODUCT: "product",
});
const COMPANY_CONTACT_PROPERTY_KEYS = new Set(["name", "email_primary", "phone_primary", "website"]);
const COMPANY_ADDRESS_PROPERTY_KEYS = new Set(["address_text", "country"]);
const COMPANY_SYSTEM_PROPERTY_KEYS = new Set(["owner_user_id", "created_by_user_id", "updated_by_user_id", "created_at", "updated_at"]);
const PERSON_CONTACT_PROPERTY_KEYS = new Set(["first_name", "last_name", "email_primary", "phone_primary"]);
const PERSON_ADDRESS_PROPERTY_KEYS = new Set(["address_text", "country"]);
const PERSON_SYSTEM_PROPERTY_KEYS = new Set(["owner_user_id", "created_by_user_id", "updated_by_user_id", "created_at", "updated_at"]);
const buildEditDealSelectionToken = (kind, id) => {
  const normalizedId = Number(id || 0);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return "";
  const normalizedKind = String(kind || "").trim().toLowerCase();
  if (!Object.values(EDIT_DEAL_SELECTION_KINDS).includes(normalizedKind)) return "";
  return `${normalizedKind}:${Math.trunc(normalizedId)}`;
};
const parseEditDealSelectionToken = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  if (/^\d+$/.test(normalized)) {
    const productId = Number(normalized);
    return Number.isFinite(productId) && productId > 0
      ? { kind: EDIT_DEAL_SELECTION_KINDS.PRODUCT, id: Math.trunc(productId) }
      : null;
  }
  const separatorIndex = normalized.indexOf(":");
  if (separatorIndex <= 0) return null;
  const kind = normalized.slice(0, separatorIndex).trim().toLowerCase();
  const rawId = normalized.slice(separatorIndex + 1).trim();
  if (!Object.values(EDIT_DEAL_SELECTION_KINDS).includes(kind) || !/^\d+$/.test(rawId)) return null;
  const parsedId = Number(rawId);
  if (!Number.isFinite(parsedId) || parsedId <= 0) return null;
  return {
    kind,
    id: Math.trunc(parsedId),
  };
};
export default {
  name: "NcrmDealDetails",
  components: {
    NcrmRecordLayout,
    NcrmRecordHeader,
    NcrmActivityTimeline,
    NcrmRecordEditDialog,
    NcrmRecordHistoryTimeline,
    NcrmRecordPropertiesCard,
    NcrmPropertyLayoutDrawer,
    NcrmDealLostReasonDialog,
    NcrmDealWonHandoffDialog,
    NcrmMailjetActivityPanel,
  },
  props: {
    id: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      deal: null,
      relatedPropertiesLoading: false,
      companyRecord: null,
      personRecord: null,
      stages: [],
      ownerOptions: [],
      ownerLoading: false,
      ownerRequestSeq: 0,
      ownerSearchDebounceId: null,
      inlineOwnerLoading: false,
      ownerMenuOpen: false,
      inlineStageLoading: false,
      pendingStageChange: null,
      pendingStageChangeClearTimerId: null,
      mainTab: "activity",
      activityComposerOpen: false,
      editDialog: false,
      editLoading: false,
      editForm: {
        name: "",
        owner_user_id: null,
        stage_id: null,
        status: "open",
        product_ids: [],
        expected_close_date: "",
        ncrm_company_id: null,
        ncrm_person_id: null,
        notes: "",
      },
      editProductsLoading: false,
      editCompaniesLoading: false,
      editPeopleLoading: false,
      editProductOptions: [],
      editCompanyOptions: [],
      editPersonOptions: [],
      editProductCatalogById: {},
      wonHandoffDialog: false,
      pendingWonHandoffRequest: null,
      lostReasonDialog: false,
      lostReasonLoading: false,
      pendingLostReasonRequest: null,
      editProductRequestSeq: 0,
      editCompanyRequestSeq: 0,
      editPersonRequestSeq: 0,
      editProductSearchDebounceId: null,
      editCompanySearchDebounceId: null,
      editPersonSearchDebounceId: null,
      deleteDialog: false,
      deleteLoading: false,
      deleteForm: {
        confirmText: "",
        deleteReason: "",
      },
      lineItemsLoading: false,
      lineItems: [],
      lineItemsSummary: {
        subtotal: 0,
        total: 0,
        count: 0,
      },
      productsLoading: false,
      productsCatalog: [],
      productRequestSeq: 0,
      productSearchDebounceId: null,
      lineItemDialog: false,
      lineItemSaving: false,
      editingLineItemId: null,
      lineItemForm: {
        product_id: null,
        name: "",
        product_type: "digital_one_time",
        quantity: "1",
        unit_price: "0",
        discount_type: "none",
        discount_value: "0",
        currency: "MDL",
        billing_period: "",
        is_active_subscription: true,
        notes: "",
      },
      lineItemDeleteDialog: false,
      lineItemDeleteLoading: false,
      selectedLineItemForDelete: null,
      propertyLayoutDrawer: false,
      propertyLayoutTarget: "company",
      propertyLayoutLoadedByTarget: {
        company: false,
        person: false,
      },
      propertyLayoutAvailableKeysByTarget: {
        company: [],
        person: [],
      },
      propertyLayoutVisibleKeysByTarget: {
        company: [],
        person: [],
      },
      propertyLayoutLoadingByTarget: {
        company: false,
        person: false,
      },
      propertyLayoutSavingByTarget: {
        company: false,
        person: false,
      },
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    dealId() {
      return Number(this.id || this.$route?.params?.id || 0) || null;
    },
    recordTitle() {
      if (!this.dealId) return this.$t("ncrm.deals.details.titleFallback", { id: "-" });
      return this.deal?.name || this.$t("ncrm.deals.details.titleFallback", { id: this.dealId });
    },
    recordSubtitle() {
      return this.deal?.pipeline_name || "";
    },
    headerChips() {
      return [
        {
          text: this.statusLabel(this.deal?.status),
          color: this.statusColor(this.deal?.status),
          variant: "tonal",
        },
        {
          text: this.deal?.stage_name || "-",
          color: "primary",
          variant: "outlined",
        },
      ];
    },
    headerKpis() {
      return [
        {
          key: "owner",
          label: this.$t("ncrm.deals.details.fields.owner"),
          value: this.ownerLabel(this.deal),
          icon: "mdi-account",
        },
        {
          key: "value",
          label: this.$t("ncrm.deals.details.fields.expectedValue"),
          value: this.formatCurrency(this.deal?.expected_value),
          icon: "mdi-currency-usd",
        },
        {
          key: "close",
          label: this.$t("ncrm.deals.details.fields.expectedCloseDate"),
          value: this.formatDate(this.deal?.expected_close_date),
          icon: "mdi-calendar-clock",
        },
        {
          key: "activities",
          label: this.$t("ncrm.deals.details.context.openActivities"),
          value: this.formatNumber(this.deal?.open_activity_count),
          icon: "mdi-calendar-check-outline",
        },
      ];
    },
    emptyPropertyValueLabel() {
      return this.$t("ncrm.contacts.details.propertyEmpty.notSet");
    },
    relatedPropertySkeletonGroups() {
      return RELATED_PROPERTY_SKELETON_GROUPS;
    },
    companyPropertyLayoutEndpoint() {
      return buildNcrmPropertyLayoutEndpoint("company", DEAL_COMPANY_PROPERTY_LAYOUT_SLOT);
    },
    personPropertyLayoutEndpoint() {
      return buildNcrmPropertyLayoutEndpoint("person", DEAL_PERSON_PROPERTY_LAYOUT_SLOT);
    },
    activePropertyLayoutTarget() {
      return this.normalizePropertyLayoutTarget(this.propertyLayoutTarget);
    },
    activePropertyLayoutItems() {
      return this.propertyLayoutItemsForTarget(this.activePropertyLayoutTarget);
    },
    activePropertyLayoutVisibleKeys() {
      return this.propertyLayoutVisibleKeysForTarget(this.activePropertyLayoutTarget);
    },
    activePropertyLayoutLoading() {
      return Boolean(this.propertyLayoutLoadingByTarget?.[this.activePropertyLayoutTarget]);
    },
    activePropertyLayoutSaving() {
      return Boolean(this.propertyLayoutSavingByTarget?.[this.activePropertyLayoutTarget]);
    },
    dealPropertyRows() {
      return [
        {
          key: "name",
          label: this.$t("ncrm.deals.details.fields.name"),
          value: String(this.deal?.name || "").trim() || "-",
        },
        {
          key: "owner",
          label: this.$t("ncrm.deals.details.fields.owner"),
          value: String(this.deal?.owner_name || "").trim() || "-",
        },
        {
          key: "pipeline",
          label: this.$t("ncrm.deals.details.fields.pipeline"),
          value: String(this.deal?.pipeline_name || "").trim() || "-",
        },
        {
          key: "stage",
          label: this.$t("ncrm.deals.details.fields.stage"),
          value: String(this.deal?.stage_name || "").trim() || "-",
        },
        {
          key: "expected_value",
          label: this.$t("ncrm.deals.details.fields.expectedValue"),
          value: this.formatCurrency(this.deal?.expected_value),
        },
        {
          key: "expected_close_date",
          label: this.$t("ncrm.deals.details.fields.expectedCloseDate"),
          value: this.formatDate(this.deal?.expected_close_date),
        },
        {
          key: "status",
          label: this.$t("ncrm.deals.details.fields.status"),
          value: this.statusLabel(this.deal?.status),
        },
      ];
    },
    dealContextRows() {
      return [
        {
          key: "company",
          label: this.$t("ncrm.deals.details.context.company"),
          value: String(this.deal?.company_name || "").trim() || "-",
        },
        {
          key: "person",
          label: this.$t("ncrm.deals.details.context.person"),
          value: String(this.deal?.person_name || "").trim() || "-",
        },
        {
          key: "source_lead",
          label: this.$t("ncrm.deals.details.context.sourceLead"),
          value: this.sourceLeadLabel,
        },
        {
          key: "open_activities",
          label: this.$t("ncrm.deals.details.context.openActivities"),
          value: this.formatNumber(this.deal?.open_activity_count),
        },
        {
          key: "updated_at",
          label: this.$t("ncrm.deals.details.context.updatedAt"),
          value: this.formatDateTime(this.deal?.updated_at),
        },
      ];
    },
    sourceCompanyRoute() {
      const companyId = Number(this.deal?.ncrm_company_id || 0) || null;
      if (!companyId) return null;
      return {
        name: "NcrmCompanyDetails",
        params: { id: String(companyId) },
      };
    },
    sourcePersonRoute() {
      const personId = Number(this.deal?.ncrm_person_id || 0) || null;
      if (!personId) return null;
      return {
        name: "NcrmPersonDetails",
        params: { id: String(personId) },
      };
    },
    sourceLeadRoute() {
      const leadId = Number(this.deal?.lead_entry_id || 0) || null;
      if (!leadId) return null;
      return {
        name: "NcrmLeadDetails",
        params: { id: String(leadId) },
      };
    },
    sourceLeadLabel() {
      const leadId = Number(this.deal?.lead_entry_id || 0) || null;
      if (!leadId) return this.$t("ncrm.deals.details.context.createdManually");
      return `#${leadId}`;
    },
    isDealCreatedManually() {
      return !this.sourceLeadRoute;
    },
    companyPropertyRows() {
      return [
        {
          key: "name",
          label: this.$t("ncrm.contacts.details.company.fields.name"),
          value: this.formatRelatedValue(this.companyRecord?.name),
          rawValue: this.companyRecord?.name,
          section: this.resolveCompanyPropertySection("name"),
        },
        {
          key: "business_category",
          label: this.$t("ncrm.contacts.details.company.fields.businessCategory"),
          value: this.formatRelatedValue(this.companyRecord?.business_category),
          rawValue: this.companyRecord?.business_category,
          section: this.resolveCompanyPropertySection("business_category"),
        },
        {
          key: "email_primary",
          label: this.$t("ncrm.contacts.details.company.fields.emailPrimary"),
          value: this.formatRelatedValue(this.companyRecord?.email_primary),
          rawValue: this.companyRecord?.email_primary,
          section: this.resolveCompanyPropertySection("email_primary"),
        },
        {
          key: "phone_primary",
          label: this.$t("ncrm.contacts.details.company.fields.phonePrimary"),
          value: this.formatRelatedValue(this.companyRecord?.phone_primary),
          rawValue: this.companyRecord?.phone_primary,
          section: this.resolveCompanyPropertySection("phone_primary"),
        },
        {
          key: "website",
          label: this.$t("ncrm.contacts.details.company.fields.website"),
          value: this.formatRelatedValue(this.companyRecord?.website),
          rawValue: this.companyRecord?.website,
          section: this.resolveCompanyPropertySection("website"),
        },
        {
          key: "address_text",
          label: this.$t("ncrm.contacts.details.company.fields.address"),
          value: this.formatRelatedValue(this.companyRecord?.address_text),
          rawValue: this.companyRecord?.address_text,
          section: this.resolveCompanyPropertySection("address_text"),
        },
        {
          key: "country",
          label: this.$t("ncrm.contacts.details.company.fields.country"),
          value: this.formatRelatedValue(this.companyRecord?.country),
          rawValue: this.companyRecord?.country,
          section: this.resolveCompanyPropertySection("country"),
        },
        {
          key: "source",
          label: this.$t("ncrm.contacts.details.company.fields.source"),
          value: this.formatRelatedValue(this.companyRecord?.source),
          rawValue: this.companyRecord?.source,
          section: this.resolveCompanyPropertySection("source"),
        },
        {
          key: "owner_user_id",
          label: this.$t("ncrm.contacts.details.company.fields.ownerUser"),
          value: this.formatRelatedReferenceValue(this.companyRecord?.owner_user_id, this.companyRecord?.owner_user_name),
          rawValue: this.companyRecord?.owner_user_name || this.companyRecord?.owner_user_id,
          section: this.resolveCompanyPropertySection("owner_user_id"),
        },
        {
          key: "created_by_user_id",
          label: this.$t("ncrm.contacts.details.company.fields.createdByUser"),
          value: this.formatRelatedReferenceValue(this.companyRecord?.created_by_user_id, this.companyRecord?.created_by_user_name),
          rawValue: this.companyRecord?.created_by_user_name || this.companyRecord?.created_by_user_id,
          section: this.resolveCompanyPropertySection("created_by_user_id"),
        },
        {
          key: "updated_by_user_id",
          label: this.$t("ncrm.contacts.details.company.fields.updatedByUser"),
          value: this.formatRelatedReferenceValue(this.companyRecord?.updated_by_user_id, this.companyRecord?.updated_by_user_name),
          rawValue: this.companyRecord?.updated_by_user_name || this.companyRecord?.updated_by_user_id,
          section: this.resolveCompanyPropertySection("updated_by_user_id"),
        },
        {
          key: "created_at",
          label: this.$t("ncrm.contacts.details.company.fields.createdAt"),
          value: this.formatDateTime(this.companyRecord?.created_at),
          rawValue: this.companyRecord?.created_at,
          section: this.resolveCompanyPropertySection("created_at"),
        },
        {
          key: "updated_at",
          label: this.$t("ncrm.contacts.details.company.fields.updatedAt"),
          value: this.formatDateTime(this.companyRecord?.updated_at),
          rawValue: this.companyRecord?.updated_at,
          section: this.resolveCompanyPropertySection("updated_at"),
        },
      ];
    },
    companyCustomPropertyRows() {
      const fields = Array.isArray(this.companyRecord?.custom_fields) ? this.companyRecord.custom_fields : [];
      return fields
        .map((entry) => {
          const rawKey = String(entry?.key || entry?.field_definition_id || "").trim();
          if (!rawKey) return null;
          const layoutKey = `custom:${rawKey}`;
          return {
            key: layoutKey,
            label: String(entry?.label || "").trim() || this.humanizeRelatedFieldKey(rawKey),
            value: this.formatRelatedValue(entry?.value_text),
            rawValue: entry?.value_text,
            section: this.resolveCompanyPropertySection(layoutKey),
          };
        })
        .filter(Boolean);
    },
    personPropertyRows() {
      return [
        {
          key: "first_name",
          label: this.$t("ncrm.contacts.details.person.fields.firstName"),
          value: this.formatRelatedValue(this.personRecord?.first_name),
          rawValue: this.personRecord?.first_name,
          section: this.resolvePersonPropertySection("first_name"),
        },
        {
          key: "last_name",
          label: this.$t("ncrm.contacts.details.person.fields.lastName"),
          value: this.formatRelatedValue(this.personRecord?.last_name),
          rawValue: this.personRecord?.last_name,
          section: this.resolvePersonPropertySection("last_name"),
        },
        {
          key: "position",
          label: this.$t("ncrm.contacts.details.person.fields.position"),
          value: this.formatRelatedValue(this.personRecord?.position),
          rawValue: this.personRecord?.position,
          section: this.resolvePersonPropertySection("position"),
        },
        {
          key: "department",
          label: this.$t("ncrm.contacts.details.person.fields.department"),
          value: this.formatRelatedValue(this.personRecord?.department),
          rawValue: this.personRecord?.department,
          section: this.resolvePersonPropertySection("department"),
        },
        {
          key: "email_primary",
          label: this.$t("ncrm.contacts.details.person.fields.emailPrimary"),
          value: this.formatRelatedValue(this.personRecord?.email_primary),
          rawValue: this.personRecord?.email_primary,
          section: this.resolvePersonPropertySection("email_primary"),
        },
        {
          key: "phone_primary",
          label: this.$t("ncrm.contacts.details.person.fields.phonePrimary"),
          value: this.formatRelatedValue(this.personRecord?.phone_primary),
          rawValue: this.personRecord?.phone_primary,
          section: this.resolvePersonPropertySection("phone_primary"),
        },
        {
          key: "ncrm_company_id",
          label: this.$t("ncrm.contacts.details.person.fields.companyName"),
          value: this.formatRelatedValue(this.personRecord?.company_name || this.deal?.company_name),
          rawValue: this.personRecord?.company_name || this.deal?.company_name,
          section: this.resolvePersonPropertySection("ncrm_company_id"),
        },
        {
          key: "address_text",
          label: this.$t("ncrm.contacts.details.person.fields.address"),
          value: this.formatRelatedValue(this.personRecord?.address_text),
          rawValue: this.personRecord?.address_text,
          section: this.resolvePersonPropertySection("address_text"),
        },
        {
          key: "country",
          label: this.$t("ncrm.contacts.details.person.fields.country"),
          value: this.formatRelatedValue(this.personRecord?.country),
          rawValue: this.personRecord?.country,
          section: this.resolvePersonPropertySection("country"),
        },
        {
          key: "source",
          label: this.$t("ncrm.contacts.details.person.fields.source"),
          value: this.formatRelatedValue(this.personRecord?.source),
          rawValue: this.personRecord?.source,
          section: this.resolvePersonPropertySection("source"),
        },
        {
          key: "owner_user_id",
          label: this.$t("ncrm.contacts.details.person.fields.ownerUser"),
          value: this.formatRelatedReferenceValue(this.personRecord?.owner_user_id, this.personRecord?.owner_user_name),
          rawValue: this.personRecord?.owner_user_name || this.personRecord?.owner_user_id,
          section: this.resolvePersonPropertySection("owner_user_id"),
        },
        {
          key: "created_by_user_id",
          label: this.$t("ncrm.contacts.details.person.fields.createdByUser"),
          value: this.formatRelatedReferenceValue(this.personRecord?.created_by_user_id, this.personRecord?.created_by_user_name),
          rawValue: this.personRecord?.created_by_user_name || this.personRecord?.created_by_user_id,
          section: this.resolvePersonPropertySection("created_by_user_id"),
        },
        {
          key: "updated_by_user_id",
          label: this.$t("ncrm.contacts.details.person.fields.updatedByUser"),
          value: this.formatRelatedReferenceValue(this.personRecord?.updated_by_user_id, this.personRecord?.updated_by_user_name),
          rawValue: this.personRecord?.updated_by_user_name || this.personRecord?.updated_by_user_id,
          section: this.resolvePersonPropertySection("updated_by_user_id"),
        },
        {
          key: "created_at",
          label: this.$t("ncrm.contacts.details.person.fields.createdAt"),
          value: this.formatDateTime(this.personRecord?.created_at),
          rawValue: this.personRecord?.created_at,
          section: this.resolvePersonPropertySection("created_at"),
        },
        {
          key: "updated_at",
          label: this.$t("ncrm.contacts.details.person.fields.updatedAt"),
          value: this.formatDateTime(this.personRecord?.updated_at),
          rawValue: this.personRecord?.updated_at,
          section: this.resolvePersonPropertySection("updated_at"),
        },
      ];
    },
    personCustomPropertyRows() {
      const fields = Array.isArray(this.personRecord?.custom_fields) ? this.personRecord.custom_fields : [];
      return fields
        .map((entry) => {
          const rawKey = String(entry?.key || entry?.field_definition_id || "").trim();
          if (!rawKey) return null;
          const layoutKey = `custom:${rawKey}`;
          return {
            key: layoutKey,
            label: String(entry?.label || "").trim() || this.humanizeRelatedFieldKey(rawKey),
            value: this.formatRelatedValue(entry?.value_text),
            rawValue: entry?.value_text,
            section: this.resolvePersonPropertySection(layoutKey),
          };
        })
        .filter(Boolean);
    },
    companyPropertyRowsForLayout() {
      return [...this.companyPropertyRows, ...this.companyCustomPropertyRows];
    },
    personPropertyRowsForLayout() {
      return [...this.personPropertyRows, ...this.personCustomPropertyRows];
    },
    companyPropertyLayoutItems() {
      return resolveNcrmPropertyLayoutItems(this.companyPropertyRowsForLayout);
    },
    personPropertyLayoutItems() {
      return resolveNcrmPropertyLayoutItems(this.personPropertyRowsForLayout);
    },
    companyPropertyRowMap() {
      return buildNcrmPropertyRowMap(this.companyPropertyRowsForLayout);
    },
    personPropertyRowMap() {
      return buildNcrmPropertyRowMap(this.personPropertyRowsForLayout);
    },
    companyPropertiesRowsForDisplay() {
      return resolveNcrmOrderedPropertyRows({
        rows: this.companyPropertyRowsForLayout,
        rowMap: this.companyPropertyRowMap,
        availableKeys: this.propertyLayoutAvailableKeysForTarget("company"),
        visibleKeys: this.propertyLayoutVisibleKeysForTarget("company"),
      });
    },
    personPropertiesRowsForDisplay() {
      return resolveNcrmOrderedPropertyRows({
        rows: this.personPropertyRowsForLayout,
        rowMap: this.personPropertyRowMap,
        availableKeys: this.propertyLayoutAvailableKeysForTarget("person"),
        visibleKeys: this.propertyLayoutVisibleKeysForTarget("person"),
      });
    },
    companyPropertySectionsForDisplay() {
      return this.buildRelatedPropertySections(this.companyPropertiesRowsForDisplay);
    },
    personPropertySectionsForDisplay() {
      return this.buildRelatedPropertySections(this.personPropertiesRowsForDisplay);
    },
    stageOptions() {
      return (this.stages || []).map((stage) => ({
        id: Number(stage?.id || 0),
        label: stage?.name || `#${stage?.id}`,
      }));
    },
    currentStageId() {
      return Number(this.deal?.stage_id || 0) || null;
    },
    currentStageLabel() {
      const stageId = this.currentStageId;
      if (!stageId) return "-";
      const option = this.stageOptions.find((item) => item.id === stageId);
      if (option?.label) return option.label;
      return String(this.deal?.stage_name || "").trim() || "-";
    },
    statusOptions() {
      return [
        { value: "open", label: this.statusLabel("open") },
        { value: "won", label: this.statusLabel("won") },
        { value: "lost", label: this.statusLabel("lost") },
      ];
    },
    quickOwnerOptions() {
      return (this.ownerOptions || [])
        .map((owner) => ({
          id: Number(owner?.id || 0) || null,
          label: String(owner?.label || "").trim() || `#${Number(owner?.id || 0) || "-"}`,
        }))
        .filter((entry) => entry.id);
    },
    currentOwnerUserId() {
      return Number(this.deal?.owner_user_id || 0) || null;
    },
    ownerQuickActionLoading() {
      return this.inlineOwnerLoading || this.ownerLoading || this.loading;
    },
    selectedEditStageType() {
      return this.resolveStageTypeById(this.editForm?.stage_id);
    },
    requiresEditStatusReason() {
      const currentStatus = String(this.deal?.status || "open").trim().toLowerCase();
      const currentStageType = this.resolveStageTypeById(this.deal?.stage_id);
      const requestedStatus = String(this.editForm?.status || currentStatus || "open").trim().toLowerCase();
      const requestedStageType = this.resolveStageTypeById(this.editForm?.stage_id);
      const isCurrentlyLost = currentStatus === "lost" || currentStageType === "lost";
      const statusTransitionsToLost = requestedStatus === "lost" && currentStatus !== "lost";
      const stageTransitionsToLost = requestedStageType === "lost" && !isCurrentlyLost;
      return statusTransitionsToLost || stageTransitionsToLost;
    },
    shouldRequireStageActivityBeforeChange() {
      return this.deal?.pipeline_auto_create_task_if_missing_after_change === true ||
        Number(this.deal?.pipeline_auto_create_task_if_missing_after_change || 0) === 1 ||
        this.deal?.auto_create_task_if_missing_after_change === true ||
        Number(this.deal?.auto_create_task_if_missing_after_change || 0) === 1;
    },
    ...dealDetailsActivityComputed,
    overviewRows() {
      return [
        {
          key: "company",
          label: this.$t("ncrm.deals.details.context.company"),
          value: this.deal?.company_name || "",
        },
        {
          key: "person",
          label: this.$t("ncrm.deals.details.context.person"),
          value: this.deal?.person_name || "",
        },
        {
          key: "owner",
          label: this.$t("ncrm.deals.details.fields.owner"),
          value: this.deal?.owner_name || "",
        },
        {
          key: "notes",
          label: this.$t("ncrm.deals.details.fields.notes"),
          value: this.deal?.notes || "",
        },
      ].filter((row) => String(row.value || "").trim());
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-deal-details-select-menu",
        zIndex: 2710,
      };
    },
    lineItemsCurrency() {
      const firstCurrency = this.lineItems.find((item) => String(item?.currency || "").trim())?.currency;
      return String(firstCurrency || "MDL").toUpperCase();
    },
    productOptions() {
      return (this.productsCatalog || []).map((item) => ({
        id: Number(item?.id || 0) || null,
        label: item?.sku ? `${item.name} (${item.sku})` : item?.name || `#${item?.id}`,
      }));
    },
    editProductSelectionOptions() {
      const options = [];
      const seenValues = new Set();
      const existingProductIds = new Set();
      for (const item of this.lineItems || []) {
        const lineItemId = Number(item?.id || 0) || null;
        const productId = Number(item?.product_id || 0) || null;
        if (!lineItemId || !productId) continue;
        const value = buildEditDealSelectionToken(EDIT_DEAL_SELECTION_KINDS.LINE_ITEM, lineItemId);
        if (!value || seenValues.has(value)) continue;
        const name =
          String(item?.product_name || "").trim() ||
          String(item?.name_snapshot || "").trim() ||
          this.resolveEditProductName(productId) ||
          `#${productId}`;
        const amount = this.formatMoney(item?.total_amount, item?.currency);
        const billing = this.billingPeriodLabel(item?.billing_period_snapshot, item?.product_type_snapshot);
        const meta = [
          `#${lineItemId}`,
          amount !== "-" ? amount : "",
          billing !== "-" ? billing : "",
        ]
          .filter(Boolean)
          .join(" • ");
        options.push({
          value,
          label: meta ? `${name} (${meta})` : name,
        });
        seenValues.add(value);
        existingProductIds.add(productId);
      }
      for (const item of this.editProductOptions || []) {
        const productId = Number(item?.id || 0) || null;
        if (!productId || existingProductIds.has(productId)) continue;
        const value = buildEditDealSelectionToken(EDIT_DEAL_SELECTION_KINDS.PRODUCT, productId);
        if (!value || seenValues.has(value)) continue;
        options.push({
          value,
          label: String(item?.label || "").trim() || this.resolveEditProductName(productId) || `#${productId}`,
        });
        seenValues.add(value);
      }
      return options;
    },
    lineItemProductTypeOptions() {
      return [
        { value: "physical", label: this.lineItemProductTypeLabel("physical") },
        { value: "digital_one_time", label: this.lineItemProductTypeLabel("digital_one_time") },
        { value: "subscription", label: this.lineItemProductTypeLabel("subscription") },
      ];
    },
    discountTypeOptions() {
      return [
        { value: "none", label: this.$t("ncrm.deals.details.lineItems.discountTypes.none") },
        { value: "percent", label: this.$t("ncrm.deals.details.lineItems.discountTypes.percent") },
        { value: "fixed", label: this.$t("ncrm.deals.details.lineItems.discountTypes.fixed") },
      ];
    },
    billingPeriodOptions() {
      return [
        { value: "monthly", label: this.billingPeriodLabel("monthly") },
        { value: "yearly", label: this.billingPeriodLabel("yearly") },
      ];
    },
    canSubmitLineItem() {
      const name = String(this.lineItemForm.name || "").trim();
      if (!name) return false;
      const quantity = Number(this.lineItemForm.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) return false;
      const unitPrice = Number(this.lineItemForm.unit_price);
      if (!Number.isFinite(unitPrice) || unitPrice < 0) return false;
      if (String(this.lineItemForm.product_type || "").trim() === "subscription") {
        return Boolean(String(this.lineItemForm.billing_period || "").trim());
      }
      return true;
    },
    canDeleteSubmit() {
      return this.deleteForm.confirmText === "DELETE" && String(this.deleteForm.deleteReason || "").trim().length > 0;
    },
  },
  watch: {
    dealId() {
      this.loadDeal();
    },
    "$route.query.tab": {
      handler() {
        this.applyMainTabFromRoute();
      },
      immediate: true,
    },
    mainTab(value) {
      if (value !== "activity") {
        this.activityComposerOpen = false;
      }
      this.syncRouteTab(value);
    },
    "lineItemForm.product_type"(value) {
      if (value !== "subscription") {
        this.lineItemForm.billing_period = "";
        this.lineItemForm.is_active_subscription = false;
      } else if (!this.lineItemForm.billing_period) {
        this.lineItemForm.billing_period = "monthly";
        this.lineItemForm.is_active_subscription = true;
      }
    },
    "editForm.stage_id"(value, previousValue) {
      if (value === previousValue) return;
      this.maybeOpenWonHandoffFromEdit({
        nextStageId: value,
      });
      this.maybeOpenLostReasonFromEdit({
        nextStageId: value,
      });
    },
    "editForm.status"(value, previousValue) {
      if (value === previousValue) return;
      this.maybeOpenWonHandoffFromEdit({
        nextStatus: value,
      });
      this.maybeOpenLostReasonFromEdit({
        nextStatus: value,
      });
    },
    companyPropertyLayoutItems: {
      deep: true,
      handler() {
        this.syncPropertyLayoutStateForTarget("company");
      },
    },
    personPropertyLayoutItems: {
      deep: true,
      handler() {
        this.syncPropertyLayoutStateForTarget("person");
      },
    },
  },
  mounted() {
    this.loadDeal();
  },
  beforeUnmount() {
    this.clearPendingStageChange();
    if (this.editProductSearchDebounceId) clearTimeout(this.editProductSearchDebounceId);
    if (this.editCompanySearchDebounceId) clearTimeout(this.editCompanySearchDebounceId);
    if (this.editPersonSearchDebounceId) clearTimeout(this.editPersonSearchDebounceId);
  },
  methods: {
    onMailjetNotify(payload) {
      if (!payload) return;
      if (typeof this.showSnackbar === "function") {
        this.showSnackbar(payload.message, payload.color || "info");
      }
    },
    ...dealDetailsActivityMethods,
    ...dealLostReasonMethods,
    resolveMainTabFromRoute() {
      const routeTab = String(this.$route?.query?.tab || "").trim().toLowerCase();
      if (DEAL_DETAILS_TABS.includes(routeTab)) return routeTab;
      return "";
    },
    applyMainTabFromRoute() {
      const routeTab = this.resolveMainTabFromRoute();
      if (!routeTab || routeTab === this.mainTab) return;
      this.mainTab = routeTab;
    },
    syncRouteTab(tab) {
      const normalized = DEAL_DETAILS_TABS.includes(tab) ? tab : "activity";
      const current = String(this.$route?.query?.tab || "").trim().toLowerCase();
      if (current === normalized) return;
      this.$router
        .replace({
          query: {
            ...(this.$route?.query || {}),
            tab: normalized,
          },
        })
        .catch(() => {});
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    async openAddActivity(preset = {}) {
      if (!this.dealId) return;
      this.mainTab = "activity";
      const waitForNextTick = typeof this.$nextTick === "function"
        ? () => this.$nextTick()
        : () => Promise.resolve();
      await waitForNextTick();
      await waitForNextTick();
      const timeline = this.$refs?.dealActivityTimeline;
      if (timeline && typeof timeline.openComposerWithPreset === "function") {
        timeline.openComposerWithPreset(preset);
        return;
      }
      this.activityComposerOpen = true;
    },
    onDealActivityComposerToggle(isOpen) {
      this.activityComposerOpen = Boolean(isOpen);
      if (isOpen) return;
      if (!this.pendingStageChange) return;
      if (this.pendingStageChangeClearTimerId) {
        clearTimeout(this.pendingStageChangeClearTimerId);
      }
      this.pendingStageChangeClearTimerId = setTimeout(() => {
        this.clearPendingStageChange();
      }, 0);
    },
    clearPendingStageChange() {
      if (this.pendingStageChangeClearTimerId) {
        clearTimeout(this.pendingStageChangeClearTimerId);
      }
      this.pendingStageChangeClearTimerId = null;
      this.pendingStageChange = null;
    },
    buildStageActivityPreset() {
      return {
        activity_type: "todo",
        allowed_activity_types: ["todo", "call", "email", "meeting"],
        notice_message: this.$t("ncrm.deals.details.messages.stageChangeNeedsActiveTask"),
        notice_color: "warning",
      };
    },
    async openStageChangeActivityComposer({ targetStageId = null, closeEditDialog = false, pendingEditRequest = null } = {}) {
      const normalizedStageId = Number(targetStageId || 0) || null;
      if (!normalizedStageId) return;
      this.pendingStageChange = {
        stageId: normalizedStageId,
        editRequest: pendingEditRequest,
      };
      if (closeEditDialog) this.editDialog = false;
      await this.openAddActivity(this.buildStageActivityPreset());
    },
    async onDealActivityCreated(activity) {
      if (this.pendingStageChangeClearTimerId) {
        clearTimeout(this.pendingStageChangeClearTimerId);
        this.pendingStageChangeClearTimerId = null;
      }
      const pendingStageChange = this.pendingStageChange;
      if (!pendingStageChange?.stageId) return;
      const activityType = String(activity?.activity_type || "").trim().toLowerCase();
      if (!["todo", "call", "email", "meeting"].includes(activityType)) {
        this.clearPendingStageChange();
        this.showSnackbar(this.$t("ncrm.deals.details.messages.stageActivityInvalid"), "warning");
        return;
      }
      const targetStageId = pendingStageChange.stageId;
      const pendingEditRequest = pendingStageChange.editRequest || null;
      this.clearPendingStageChange();
      this.inlineStageLoading = true;
      try {
        if (pendingEditRequest) {
          await this.applyDealEditChanges({
            ...pendingEditRequest,
            targetStageId,
          });
          this.showSnackbar(this.$t("ncrm.deals.details.messages.updated"), "success");
          await this.refreshDealActivityTimelineIfOpen();
        } else {
          await axios.patch(`/ncrm/deals/${this.dealId}/stage`, {
            stage_id: targetStageId,
          });
          this.showSnackbar(this.$t("ncrm.deals.details.messages.stageChanged"), "success");
        }
        await this.loadDeal();
      } catch (error) {
        console.error("[ncrm-deal-details] stage change after activity error", error);
        if (pendingEditRequest) this.editDialog = true;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.messages.stageChangeError"),
          "error"
        );
      } finally {
        this.inlineStageLoading = false;
      }
    },
    ensureActiveTaskBeforeStageChange({ targetStageId = null, closeEditDialog = false, pendingEditRequest = null } = {}) {
      if (!this.deal) return true;
      const nextStatus = this.resolveStatusFromStageId(targetStageId);
      if (nextStatus !== "open") return true;
      if (!this.shouldRequireStageActivityBeforeChange) return true;
      const openActivityCount = Number(this.deal?.open_activity_count || 0);
      if (Number.isFinite(openActivityCount) && openActivityCount > 0) return true;
      const composerPayload = { targetStageId, closeEditDialog };
      if (pendingEditRequest) composerPayload.pendingEditRequest = pendingEditRequest;
      this.openStageChangeActivityComposer(composerPayload);
      return false;
    },
    async applyOwnerInline(ownerUserId) {
      const normalizedOwnerId = Number(ownerUserId || 0) || null;
      const currentOwnerId = Number(this.deal?.owner_user_id || 0) || null;
      this.ownerMenuOpen = false;
      if (!this.dealId || !normalizedOwnerId || normalizedOwnerId === currentOwnerId || this.ownerQuickActionLoading) {
        return;
      }
      this.inlineOwnerLoading = true;
      try {
        await axios.patch(`/ncrm/deals/${this.dealId}`, {
          owner_user_id: normalizedOwnerId,
        });
        this.showSnackbar(this.$t("ncrm.deals.details.messages.ownerUpdated"), "success");
        await this.loadDeal();
      } catch (error) {
        console.error("[ncrm-deal-details] owner quick action error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.messages.quickActionError"),
          "error"
        );
      } finally {
        this.inlineOwnerLoading = false;
      }
    },
    handleHeaderKpiClick(kpi) {
      const key = String(kpi?.key || "").trim();
      if (key !== "value") return;
      this.mainTab = "products";
      if (typeof this.$nextTick !== "function") return;
      this.$nextTick(() => {
        const target = this.$refs?.lineItemsSection;
        if (target && typeof target.scrollIntoView === "function") {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async refreshDealActivityContext() {
      if (!this.dealId) return;
      try {
        const { data } = await axios.get(`/ncrm/deals/${this.dealId}`);
        if (!data || !this.deal) {
          this.deal = data || this.deal;
          return;
        }
        this.deal = {
          ...this.deal,
          open_activity_count: data.open_activity_count,
          updated_at: data.updated_at,
        };
      } catch (error) {
        console.error("[ncrm-deal-details] refresh activity context error", error);
      }
    },
    formatNumber(value) {
      return formatDisplayNumber(toNumber(value, 0));
    },
    formatCurrency(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return "-";
      return formatDisplayAmount(parsed);
    },
    ownerLabel(entry = {}) {
      const source = entry && typeof entry === "object" ? entry : {};
      const label = String(source.owner_name || "").trim();
      if (label) return label;
      const ownerId = Number(source.owner_user_id || 0) || null;
      return ownerId ? `#${ownerId}` : this.emptyPropertyValueLabel;
    },
    formatMoney(value, currency = "MDL") {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return "-";
      const code = String(currency || "MDL").trim().toUpperCase() || "MDL";
      return `${formatDisplayAmount(parsed)} ${code}`;
    },
    formatQuantity(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return "-";
      if (Number.isInteger(parsed)) return String(parsed);
      return formatDisplayNumber(parsed);
    },
    formatDate(value) {
      const normalized = normalizeDate(value);
      if (!normalized) return "-";
      const parsed = new Date(`${normalized}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return normalized;
      return formatDisplayDate(parsed);
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      return formatDisplayDateTime(parsed);
    },
    humanizeRelatedFieldKey(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "";
      return normalized
        .replace(/_/g, " ")
        .replace(/\bid\b/g, "ID")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatRelatedValue(value) {
      if (value === null || value === undefined) return this.emptyPropertyValueLabel;
      const normalized = String(value).trim();
      return normalized || this.emptyPropertyValueLabel;
    },
    formatRelatedReferenceValue(idValue, displayName) {
      const normalizedName = String(displayName || "").trim();
      if (normalizedName) return normalizedName;
      if (idValue === null || idValue === undefined) return this.emptyPropertyValueLabel;
      const normalizedId = String(idValue).trim();
      return normalizedId || this.emptyPropertyValueLabel;
    },
    isRelatedPropertyRowEmpty(value) {
      if (value === null || value === undefined) return true;
      const normalized = String(value).trim();
      return !normalized || normalized === "-" || normalized === this.emptyPropertyValueLabel;
    },
    shouldDisplayRelatedPropertyRow(row) {
      return !this.isRelatedPropertyRowEmpty(row?.value);
    },
    resolveCompanyPropertySection(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "business";
      if (normalized.startsWith("custom:")) return "customFields";
      if (COMPANY_SYSTEM_PROPERTY_KEYS.has(normalized)) return "system";
      if (COMPANY_ADDRESS_PROPERTY_KEYS.has(normalized)) return "address";
      if (COMPANY_CONTACT_PROPERTY_KEYS.has(normalized)) return "contact";
      return "business";
    },
    resolvePersonPropertySection(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "business";
      if (normalized.startsWith("custom:")) return "customFields";
      if (PERSON_SYSTEM_PROPERTY_KEYS.has(normalized)) return "system";
      if (PERSON_ADDRESS_PROPERTY_KEYS.has(normalized)) return "address";
      if (PERSON_CONTACT_PROPERTY_KEYS.has(normalized)) return "contact";
      return "business";
    },
    buildRelatedPropertySections(rows = []) {
      const grouped = RELATED_PROPERTY_SECTION_ORDER.reduce((acc, sectionKey) => {
        acc[sectionKey] = [];
        return acc;
      }, {});
      for (const row of rows || []) {
        const sectionKey = String(row?.section || "").trim();
        const normalizedSectionKey = grouped[sectionKey] ? sectionKey : "business";
        if (!this.shouldDisplayRelatedPropertyRow(row)) continue;
        grouped[normalizedSectionKey].push(row);
      }
      return RELATED_PROPERTY_SECTION_ORDER
        .map((sectionKey) => ({
          key: sectionKey,
          label: this.$t(`ncrm.contacts.details.propertySections.${sectionKey}`),
          rows: grouped[sectionKey],
        }))
        .filter((section) => section.rows.length > 0);
    },
    resolveRelatedPropertyRowLink(row) {
      return resolveNcrmPropertyLinkMeta(row?.key, row?.rawValue || "");
    },
    canCopyRelatedPropertyRow(row) {
      return Boolean(String(this.resolveRelatedPropertyRowLink(row)?.copyValue || "").trim());
    },
    async copyRelatedPropertyRowValue(row) {
      const copyValue = String(this.resolveRelatedPropertyRowLink(row)?.copyValue || "").trim();
      if (!copyValue) return;
      let copied = false;
      if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(copyValue);
          copied = true;
        } catch (_error) {
          copied = false;
        }
      }
      if (!copied && typeof document !== "undefined") {
        const textarea = document.createElement("textarea");
        textarea.value = copyValue;
        textarea.setAttribute("readonly", "readonly");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        if (typeof document.execCommand === "function") {
          copied = document.execCommand("copy");
        }
        document.body.removeChild(textarea);
      }
      if (copied) {
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copySuccess"), "success");
      } else {
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copyError"), "error");
      }
    },
    resolveRelatedTimestampTooltip(rowKey, row = {}) {
      const normalizedKey = String(rowKey || "").trim();
      if (normalizedKey !== "created_at" && normalizedKey !== "updated_at") return "";
      const tooltipValue = formatNcrmTimestampTooltip(row?.rawValue);
      if (!tooltipValue) return "";
      return this.$t("ncrm.contacts.details.propertyInteractions.timestampTooltip", {
        value: tooltipValue,
      });
    },
    normalizePropertyLayoutTarget(value) {
      const normalized = String(value || "").trim().toLowerCase();
      return normalized === "person" ? "person" : "company";
    },
    propertyLayoutEndpointForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      return normalizedTarget === "person" ? this.personPropertyLayoutEndpoint : this.companyPropertyLayoutEndpoint;
    },
    propertyLayoutItemsForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      return normalizedTarget === "person" ? this.personPropertyLayoutItems : this.companyPropertyLayoutItems;
    },
    propertyLayoutAvailableKeysForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      return Array.isArray(this.propertyLayoutAvailableKeysByTarget?.[normalizedTarget])
        ? this.propertyLayoutAvailableKeysByTarget[normalizedTarget]
        : [];
    },
    propertyLayoutVisibleKeysForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      return Array.isArray(this.propertyLayoutVisibleKeysByTarget?.[normalizedTarget])
        ? this.propertyLayoutVisibleKeysByTarget[normalizedTarget]
        : [];
    },
    propertyLayoutLoadedForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      return Boolean(this.propertyLayoutLoadedByTarget?.[normalizedTarget]);
    },
    setPropertyLayoutStateForTarget(target, { availableKeys = null, visibleKeys = null, loaded = null } = {}) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      if (Array.isArray(availableKeys)) {
        this.propertyLayoutAvailableKeysByTarget = {
          ...(this.propertyLayoutAvailableKeysByTarget || {}),
          [normalizedTarget]: dedupeNcrmPropertyLayoutKeys(availableKeys),
        };
      }
      if (Array.isArray(visibleKeys)) {
        this.propertyLayoutVisibleKeysByTarget = {
          ...(this.propertyLayoutVisibleKeysByTarget || {}),
          [normalizedTarget]: dedupeNcrmPropertyLayoutKeys(visibleKeys),
        };
      }
      if (typeof loaded === "boolean") {
        this.propertyLayoutLoadedByTarget = {
          ...(this.propertyLayoutLoadedByTarget || {}),
          [normalizedTarget]: loaded,
        };
      }
    },
    setPropertyLayoutLoadingForTarget(target, isLoading) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      this.propertyLayoutLoadingByTarget = {
        ...(this.propertyLayoutLoadingByTarget || {}),
        [normalizedTarget]: Boolean(isLoading),
      };
    },
    setPropertyLayoutSavingForTarget(target, isSaving) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      this.propertyLayoutSavingByTarget = {
        ...(this.propertyLayoutSavingByTarget || {}),
        [normalizedTarget]: Boolean(isSaving),
      };
    },
    resetPropertyLayoutStateForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      this.setPropertyLayoutStateForTarget(normalizedTarget, {
        availableKeys: [],
        visibleKeys: [],
        loaded: false,
      });
      this.setPropertyLayoutLoadingForTarget(normalizedTarget, false);
      this.setPropertyLayoutSavingForTarget(normalizedTarget, false);
      if (this.propertyLayoutDrawer && this.activePropertyLayoutTarget === normalizedTarget) {
        this.propertyLayoutDrawer = false;
      }
    },
    syncPropertyLayoutStateForTarget(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      const { availableKeys, visibleKeys } = resolveNcrmPropertyLayoutState({
        rows: this.propertyLayoutItemsForTarget(normalizedTarget),
        fallbackVisibleKeys: this.propertyLayoutVisibleKeysForTarget(normalizedTarget),
      });
      this.setPropertyLayoutStateForTarget(normalizedTarget, {
        availableKeys,
        visibleKeys,
        loaded: availableKeys.length ? this.propertyLayoutLoadedForTarget(normalizedTarget) : false,
      });
    },
    applyPropertyLayoutResponseForTarget(target, payload = {}) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      const { availableKeys, visibleKeys } = resolveNcrmPropertyLayoutState({
        rows: this.propertyLayoutItemsForTarget(normalizedTarget),
        payload,
        fallbackVisibleKeys: this.propertyLayoutVisibleKeysForTarget(normalizedTarget),
      });
      this.setPropertyLayoutStateForTarget(normalizedTarget, {
        availableKeys,
        visibleKeys,
        loaded: true,
      });
    },
    async fetchPropertyLayoutForTarget(target, { force = false, silent = false } = {}) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      const endpoint = this.propertyLayoutEndpointForTarget(normalizedTarget);
      if (!endpoint) {
        this.resetPropertyLayoutStateForTarget(normalizedTarget);
        return;
      }
      if (this.propertyLayoutLoadingByTarget?.[normalizedTarget]) return;
      if (this.propertyLayoutLoadedForTarget(normalizedTarget) && !force) {
        this.syncPropertyLayoutStateForTarget(normalizedTarget);
        return;
      }
      this.setPropertyLayoutLoadingForTarget(normalizedTarget, true);
      try {
        const { data } = await axios.get(endpoint);
        this.applyPropertyLayoutResponseForTarget(normalizedTarget, data || {});
      } catch (error) {
        console.error(`[ncrm-deal-details] ${normalizedTarget} property layout load error`, error);
        this.setPropertyLayoutStateForTarget(normalizedTarget, { loaded: false });
        this.syncPropertyLayoutStateForTarget(normalizedTarget);
        if (!silent) {
          this.showSnackbar(
            error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.loadError"),
            "error"
          );
        }
      } finally {
        this.setPropertyLayoutLoadingForTarget(normalizedTarget, false);
      }
    },
    openPropertyLayoutDrawer(target) {
      const normalizedTarget = this.normalizePropertyLayoutTarget(target);
      this.propertyLayoutTarget = normalizedTarget;
      this.propertyLayoutDrawer = true;
      this.fetchPropertyLayoutForTarget(normalizedTarget, {
        force: !this.propertyLayoutLoadedForTarget(normalizedTarget),
        silent: false,
      });
    },
    async savePropertyLayout(visibleKeys) {
      const target = this.activePropertyLayoutTarget;
      const endpoint = this.propertyLayoutEndpointForTarget(target);
      if (!endpoint) return;
      const availableKeys = dedupeNcrmPropertyLayoutKeys(this.propertyLayoutItemsForTarget(target).map((entry) => entry.key));
      const payloadKeys = sanitizeNcrmPropertyLayoutVisibleKeys(visibleKeys, availableKeys);
      if (!payloadKeys.length) {
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.requiredWarning"), "warning");
        return;
      }
      this.setPropertyLayoutSavingForTarget(target, true);
      try {
        const { data } = await axios.put(endpoint, {
          visible_property_keys: payloadKeys,
        });
        this.applyPropertyLayoutResponseForTarget(target, data || {});
        this.propertyLayoutDrawer = false;
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.messages.saveSuccess"), "success");
      } catch (error) {
        console.error(`[ncrm-deal-details] ${target} property layout save error`, error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.saveError"),
          "error"
        );
      } finally {
        this.setPropertyLayoutSavingForTarget(target, false);
      }
    },
    statusLabel(status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (!normalized) return "-";
      const i18nKey = `ncrm.deals.statuses.${normalized}`;
      const translated = this.$t(i18nKey);
      return translated === i18nKey ? normalized : translated;
    },
    statusColor(status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (normalized === "won") return "success";
      if (normalized === "lost") return "error";
      return "primary";
    },
    resolveStageTypeById(stageId) {
      const normalizedStageId = Number(stageId || 0) || null;
      if (!normalizedStageId) return "";
      const stage = (this.stages || []).find((item) => Number(item?.id || 0) === normalizedStageId);
      return String(stage?.stage_type || "").trim().toLowerCase();
    },
    resolveStatusFromStageId(stageId) {
      const stageType = this.resolveStageTypeById(stageId);
      if (stageType === "won" || stageType === "lost") return stageType;
      return "open";
    },
    lineItemProductTypeLabel(type) {
      const normalized = String(type || "").trim().toLowerCase();
      return this.$t(`ncrm.deals.details.lineItems.types.${normalized}`) || normalized || "-";
    },
    lineItemDiscountLabel(item = {}) {
      const normalizedDiscountType = String(item?.discount_type || "").trim().toLowerCase();
      if (normalizedDiscountType === "percent") {
        return `${this.formatNumber(item?.discount_value)}%`;
      }
      if (normalizedDiscountType === "fixed") {
        return this.formatMoney(item?.discount_value, item?.currency);
      }
      return "-";
    },
    lineItemSubscriptionLabel(item = {}) {
      const normalizedProductType = String(item?.product_type_snapshot || "").trim().toLowerCase();
      if (normalizedProductType !== "subscription") {
        return "-";
      }
      return Number(item?.is_active_subscription) === 1
        ? this.$t("ncrm.deals.details.lineItems.subscription.active")
        : this.$t("ncrm.deals.details.lineItems.subscription.inactive");
    },
    billingPeriodLabel(period, productType = "") {
      const normalized = String(period || "").trim().toLowerCase();
      if (!normalized) {
        const normalizedProductType = String(productType || "").trim().toLowerCase();
        if (normalizedProductType && normalizedProductType !== "subscription") {
          return this.$t("ncrm.deals.details.lineItems.billing.one_time");
        }
        return "-";
      }
      return this.$t(`ncrm.deals.details.lineItems.billing.${normalized}`) || normalized;
    },
    mergeOptionById(options = [], option = null) {
      const normalizedId = Number(option?.id || 0) || null;
      if (!normalizedId) return Array.isArray(options) ? options : [];
      const label = String(option?.label || "").trim() || `#${normalizedId}`;
      const normalizedOptions = Array.isArray(options) ? [...options] : [];
      const index = normalizedOptions.findIndex((item) => Number(item?.id || 0) === normalizedId);
      if (index >= 0) {
        normalizedOptions[index] = {
          ...normalizedOptions[index],
          id: normalizedId,
          label,
        };
      } else {
        normalizedOptions.push({
          id: normalizedId,
          label,
        });
      }
      return normalizedOptions;
    },
    seedOwnerOptionFromDeal() {
      const ownerId = Number(this.deal?.owner_user_id || 0) || null;
      if (!ownerId) return;
      const ownerLabel =
        String(this.deal?.owner_name || "").trim() ||
        (this.ownerOptions || []).find((option) => Number(option?.id || 0) === ownerId)?.label ||
        `#${ownerId}`;
      this.ownerOptions = this.mergeOptionById(this.ownerOptions, {
        id: ownerId,
        label: ownerLabel,
      });
    },
    resolveLineItemProductLabel(product = null, fallbackId = null) {
      const normalizedProductId = Number(product?.id || fallbackId || 0) || null;
      if (!normalizedProductId) return "";
      const name = String(product?.name || "").trim() || `#${normalizedProductId}`;
      const sku = String(product?.sku || "").trim();
      return sku ? `${name} (${sku})` : name;
    },
    seedCurrentLineItemProductOption() {
      const productId = Number(this.lineItemForm?.product_id || 0) || null;
      if (!productId) return;
      const existingProduct =
        (this.productsCatalog || []).find((item) => Number(item?.id || 0) === productId) ||
        (this.lineItems || []).find((item) => Number(item?.product_id || 0) === productId);
      if (!existingProduct) {
        this.productsCatalog = [
          {
            id: productId,
            name: `#${productId}`,
          },
        ];
        return;
      }
      const seededProduct = {
        ...existingProduct,
        id: productId,
        name:
          String(existingProduct?.name || existingProduct?.product_name || existingProduct?.name_snapshot || "").trim() ||
          `#${productId}`,
      };
      this.productsCatalog = [
        seededProduct,
        ...(this.productsCatalog || []).filter((item) => Number(item?.id || 0) !== productId),
      ];
    },
    resolveEditProductName(productId) {
      const normalizedProductId = Number(productId || 0) || null;
      if (!normalizedProductId) return "";
      const catalogEntry = this.editProductCatalogById?.[normalizedProductId];
      if (String(catalogEntry?.name || "").trim()) {
        return String(catalogEntry.name).trim();
      }
      const option = (this.editProductOptions || []).find((item) => Number(item?.id || 0) === normalizedProductId);
      if (String(option?.label || "").trim()) {
        return String(option.label).trim();
      }
      const lineItem = (this.lineItems || []).find((item) => Number(item?.product_id || 0) === normalizedProductId);
      if (String(lineItem?.name_snapshot || "").trim()) {
        return String(lineItem.name_snapshot).trim();
      }
      return `#${normalizedProductId}`;
    },
    normalizeEditProductSelection(values = []) {
      return Array.from(
        new Set(
          (Array.isArray(values) ? values : [])
            .map((value) => {
              const parsed = parseEditDealSelectionToken(value);
              return parsed ? buildEditDealSelectionToken(parsed.kind, parsed.id) : "";
            })
            .filter(Boolean)
        )
      );
    },
    resolveEditProductSelection(values = []) {
      const normalizedValues = this.normalizeEditProductSelection(values);
      const lineItemsById = new Map();
      const existingProductIds = new Set();
      for (const item of this.lineItems || []) {
        const lineItemId = Number(item?.id || 0) || null;
        const productId = Number(item?.product_id || 0) || null;
        if (!lineItemId || !productId) continue;
        lineItemsById.set(lineItemId, item);
        existingProductIds.add(productId);
      }
      const selectedExistingLineItemIds = [];
      const selectedProductIds = new Set();
      const productIdsToAttach = [];
      for (const value of normalizedValues) {
        const parsed = parseEditDealSelectionToken(value);
        if (!parsed) continue;
        if (parsed.kind === EDIT_DEAL_SELECTION_KINDS.LINE_ITEM) {
          const lineItem = lineItemsById.get(parsed.id);
          const productId = Number(lineItem?.product_id || 0) || null;
          if (!lineItem || !productId) continue;
          selectedExistingLineItemIds.push(parsed.id);
          selectedProductIds.add(productId);
          continue;
        }
        if (parsed.kind === EDIT_DEAL_SELECTION_KINDS.PRODUCT) {
          selectedProductIds.add(parsed.id);
          if (!existingProductIds.has(parsed.id)) {
            productIdsToAttach.push(parsed.id);
          }
        }
      }
      return {
        selectedValues: normalizedValues,
        selectedExistingLineItemIds: Array.from(new Set(selectedExistingLineItemIds)),
        productIdsToAttach: Array.from(new Set(productIdsToAttach)),
        selectedProductIds: Array.from(selectedProductIds),
      };
    },
    seedEditCompanyOptionFromDeal() {
      const companyId = Number(this.deal?.ncrm_company_id || 0) || null;
      if (!companyId) return;
      const companyName = String(this.deal?.company_name || "").trim() || `#${companyId}`;
      this.editCompanyOptions = this.mergeOptionById(this.editCompanyOptions, {
        id: companyId,
        label: companyName,
      });
    },
    seedEditPersonOptionFromDeal() {
      const personId = Number(this.deal?.ncrm_person_id || 0) || null;
      if (!personId) return;
      const personName = String(this.deal?.person_name || "").trim() || `#${personId}`;
      this.editPersonOptions = this.mergeOptionById(this.editPersonOptions, {
        id: personId,
        label: personName,
      });
    },
    seedEditProductOptionsFromLineItems() {
      const nextCatalogById = { ...this.editProductCatalogById };
      let nextOptions = Array.isArray(this.editProductOptions) ? [...this.editProductOptions] : [];
      for (const item of this.lineItems || []) {
        const productId = Number(item?.product_id || 0) || null;
        if (!productId) continue;
        const itemName = String(item?.name_snapshot || "").trim() || `#${productId}`;
        nextOptions = this.mergeOptionById(nextOptions, {
          id: productId,
          label: itemName,
        });
        if (!nextCatalogById[productId]) {
          nextCatalogById[productId] = {
            id: productId,
            name: itemName,
            default_unit_price: item?.unit_price ?? null,
          };
        }
      }
      this.editProductCatalogById = nextCatalogById;
      this.editProductOptions = nextOptions;
    },
    estimateEditDealExpectedValue({ selectedLineItemIds = [], productIdsToAttach = [] } = {}) {
      const selectedLineItemIdSet = new Set(
        (Array.isArray(selectedLineItemIds) ? selectedLineItemIds : [])
          .map((value) => Number(value || 0))
          .filter((value) => Number.isFinite(value) && value > 0)
      );
      const normalizedProductIdsToAttach = Array.from(
        new Set(
          (Array.isArray(productIdsToAttach) ? productIdsToAttach : [])
            .map((value) => Number(value || 0))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!selectedLineItemIdSet.size && !normalizedProductIdsToAttach.length) {
        return this.deal?.expected_value ?? null;
      }
      let hasPricedProduct = false;
      let total = 0;
      for (const item of this.lineItems || []) {
        const lineItemId = Number(item?.id || 0) || null;
        if (!lineItemId || !selectedLineItemIdSet.has(lineItemId)) continue;
        let lineTotal = toNumber(item?.total_amount, null);
        if (lineTotal === null) {
          lineTotal = toNumber(item?.unit_price, null);
        }
        if (lineTotal === null) continue;
        hasPricedProduct = true;
        total += lineTotal;
      }
      for (const productId of normalizedProductIdsToAttach) {
        const product = this.editProductCatalogById?.[productId];
        if (!product) continue;
        const unitPrice = toNumber(product?.default_unit_price, null);
        if (unitPrice === null) continue;
        hasPricedProduct = true;
        total += unitPrice;
      }
      if (!hasPricedProduct) return this.deal?.expected_value ?? null;
      return Number(total.toFixed(2));
    },
    async attachProductsToDeal({ dealId, productIds = [] } = {}) {
      const normalizedDealId = Number(dealId || 0) || null;
      const normalizedProductIds = Array.from(
        new Set(
          (Array.isArray(productIds) ? productIds : [])
            .map((value) => Number(value || 0))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!normalizedDealId || !normalizedProductIds.length) {
        return { failedCount: 0, totalCount: 0 };
      }
      const settled = await Promise.allSettled(
        normalizedProductIds.map((productId) =>
          axios.post(`/ncrm/deals/${normalizedDealId}/line-items`, {
            product_id: productId,
            quantity: 1,
          })
        )
      );
      const failedCount = settled.filter((item) => item.status === "rejected").length;
      return {
        failedCount,
        totalCount: normalizedProductIds.length,
      };
    },
    handleEditProductSearch(search) {
      if (this.editProductSearchDebounceId) clearTimeout(this.editProductSearchDebounceId);
      this.editProductSearchDebounceId = setTimeout(() => {
        this.fetchEditProductOptions(search);
      }, 220);
    },
    handleOwnerSearch(search) {
      if (this.ownerSearchDebounceId) clearTimeout(this.ownerSearchDebounceId);
      this.ownerSearchDebounceId = setTimeout(() => {
        this.fetchOwners(search);
      }, 220);
    },
    handleLineItemProductSearch(search) {
      if (this.productSearchDebounceId) clearTimeout(this.productSearchDebounceId);
      this.productSearchDebounceId = setTimeout(() => {
        this.fetchProductsCatalog(search);
      }, 220);
    },
    handleEditCompanySearch(search) {
      if (this.editCompanySearchDebounceId) clearTimeout(this.editCompanySearchDebounceId);
      this.editCompanySearchDebounceId = setTimeout(() => {
        this.fetchEditCompanyOptions(search);
      }, 220);
    },
    handleEditPersonSearch(search) {
      if (this.editPersonSearchDebounceId) clearTimeout(this.editPersonSearchDebounceId);
      this.editPersonSearchDebounceId = setTimeout(() => {
        this.fetchEditPersonOptions(search);
      }, 220);
    },
    async fetchEditProductOptions(search = "") {
      const requestSeq = ++this.editProductRequestSeq;
      this.editProductsLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/products", {
          params: {
            search: normalizedSearch || undefined,
            limit: 50,
            is_active: true,
          },
        });
        if (requestSeq !== this.editProductRequestSeq) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const nextCatalogById = { ...this.editProductCatalogById };
        let nextOptions = Array.isArray(this.editProductOptions) ? [...this.editProductOptions] : [];
        for (const item of items) {
          const id = Number(item?.id || 0) || null;
          if (!id) continue;
          const name = String(item?.name || "").trim() || `#${id}`;
          const productType = String(item?.product_type || "").trim();
          const billingPeriod =
            String(item?.billing_period || "").trim() ||
            (String(item?.product_type || "").trim().toLowerCase() !== "subscription" ? "one time" : "");
          const suffix = [productType, billingPeriod].filter(Boolean).join(" • ");
          const label = suffix ? `${name} (${suffix})` : name;
          nextOptions = this.mergeOptionById(nextOptions, { id, label });
          nextCatalogById[id] = item;
        }
        this.editProductCatalogById = nextCatalogById;
        this.editProductOptions = nextOptions;
        this.seedEditProductOptionsFromLineItems();
      } catch (error) {
        console.error("[ncrm-deal-details] load edit products error", error);
      } finally {
        if (requestSeq === this.editProductRequestSeq) {
          this.editProductsLoading = false;
        }
      }
    },
    async fetchEditCompanyOptions(search = "") {
      const requestSeq = ++this.editCompanyRequestSeq;
      this.editCompaniesLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "company",
            search: normalizedSearch || undefined,
            page: 1,
            perPage: 25,
          },
        });
        if (requestSeq !== this.editCompanyRequestSeq) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        let nextOptions = items.map((item) => {
          const id = Number(item?.id || 0) || null;
          const name = String(item?.name || "").trim() || `#${id}`;
          const email = String(item?.email_primary || "").trim();
          const phone = String(item?.phone_primary || "").trim();
          const detail = [email, phone].filter(Boolean).join(" • ");
          return {
            id,
            label: detail ? `${name} (${detail})` : name,
          };
        });
        const selectedCompanyId = Number(this.editForm?.ncrm_company_id || 0) || null;
        if (selectedCompanyId) {
          const existingOption =
            this.editCompanyOptions.find((option) => Number(option?.id || 0) === selectedCompanyId) ||
            (Number(this.deal?.ncrm_company_id || 0) === selectedCompanyId
              ? {
                  id: selectedCompanyId,
                  label: String(this.deal?.company_name || "").trim() || `#${selectedCompanyId}`,
                }
              : null);
          if (existingOption) {
            nextOptions = this.mergeOptionById(nextOptions, existingOption);
          }
        }
        this.editCompanyOptions = nextOptions;
      } catch (error) {
        console.error("[ncrm-deal-details] load edit companies error", error);
      } finally {
        if (requestSeq === this.editCompanyRequestSeq) {
          this.editCompaniesLoading = false;
        }
      }
    },
    async fetchEditPersonOptions(search = "") {
      const requestSeq = ++this.editPersonRequestSeq;
      this.editPeopleLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "person",
            search: normalizedSearch || undefined,
            page: 1,
            perPage: 25,
          },
        });
        if (requestSeq !== this.editPersonRequestSeq) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        let nextOptions = items.map((item) => {
          const id = Number(item?.id || 0) || null;
          const firstName = String(item?.first_name || "").trim();
          const lastName = String(item?.last_name || "").trim();
          const fullName = `${firstName} ${lastName}`.trim() || `#${id}`;
          const company = String(item?.company_name || "").trim();
          const email = String(item?.email_primary || "").trim();
          const detail = [company, email].filter(Boolean).join(" • ");
          return {
            id,
            label: detail ? `${fullName} (${detail})` : fullName,
          };
        });
        const selectedPersonId = Number(this.editForm?.ncrm_person_id || 0) || null;
        if (selectedPersonId) {
          const existingOption =
            this.editPersonOptions.find((option) => Number(option?.id || 0) === selectedPersonId) ||
            (Number(this.deal?.ncrm_person_id || 0) === selectedPersonId
              ? {
                  id: selectedPersonId,
                  label: String(this.deal?.person_name || "").trim() || `#${selectedPersonId}`,
                }
              : null);
          if (existingOption) {
            nextOptions = this.mergeOptionById(nextOptions, existingOption);
          }
        }
        this.editPersonOptions = nextOptions;
      } catch (error) {
        console.error("[ncrm-deal-details] load edit people error", error);
      } finally {
        if (requestSeq === this.editPersonRequestSeq) {
          this.editPeopleLoading = false;
        }
      }
    },
    async fetchOwners(search = "") {
      const requestSeq = ++this.ownerRequestSeq;
      this.ownerLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/deals/owners", {
          params: {
            search: normalizedSearch || undefined,
            limit: 100,
          },
        });
        if (requestSeq !== this.ownerRequestSeq) return;
        this.ownerOptions = (Array.isArray(data?.items) ? data.items : []).map((item) => ({
          id: Number(item?.id || 0),
          label: item?.display_name || item?.name || item?.email || `#${item?.id}`,
        }));
        this.seedOwnerOptionFromDeal();
      } catch (error) {
        console.error("[ncrm-deal-details] load owners error", error);
      } finally {
        if (requestSeq === this.ownerRequestSeq) {
          this.ownerLoading = false;
        }
      }
    },
    async fetchStages() {
      const pipelineId = Number(this.deal?.pipeline_id || 0) || null;
      if (!pipelineId) {
        this.stages = [];
        return;
      }
      const { data } = await axios.get(`/ncrm/deals/pipelines/${pipelineId}/stages`, {
        params: { includeInactive: false },
      });
      this.stages = Array.isArray(data?.items) ? data.items : [];
    },
    async fetchProductsCatalog(search = "") {
      const requestSeq = ++this.productRequestSeq;
      this.productsLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/products", {
          params: {
            search: normalizedSearch || undefined,
            limit: 50,
            page: 1,
            is_active: true,
          },
        });
        if (requestSeq !== this.productRequestSeq) return;
        let nextCatalog = Array.isArray(data?.items) ? data.items : [];
        const selectedProductId = Number(this.lineItemForm?.product_id || 0) || null;
        if (selectedProductId) {
          const selectedProduct =
            (this.productsCatalog || []).find((item) => Number(item?.id || 0) === selectedProductId) ||
            (this.lineItems || []).find((item) => Number(item?.product_id || 0) === selectedProductId);
          if (selectedProduct) {
            const selectedEntry = {
              ...selectedProduct,
              id: selectedProductId,
              name:
                String(selectedProduct?.name || selectedProduct?.product_name || selectedProduct?.name_snapshot || "").trim() ||
                `#${selectedProductId}`,
            };
            if (!nextCatalog.some((item) => Number(item?.id || 0) === selectedProductId)) {
              nextCatalog = [selectedEntry, ...nextCatalog];
            }
          }
        }
        this.productsCatalog = nextCatalog;
      } catch (error) {
        console.error("[ncrm-deal-details] load products catalog error", error);
      } finally {
        if (requestSeq === this.productRequestSeq) {
          this.productsLoading = false;
        }
      }
    },
    async fetchLineItems() {
      if (!this.dealId) return;
      this.lineItemsLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/deals/${this.dealId}/line-items`);
        this.lineItems = Array.isArray(data?.items) ? data.items : [];
        this.lineItemsSummary = {
          subtotal: Number(data?.summary?.subtotal || 0),
          total: Number(data?.summary?.total || 0),
          count: Number(data?.summary?.count || 0),
        };
      } catch (error) {
        console.error("[ncrm-deal-details] load line items error", error);
        this.lineItems = [];
        this.lineItemsSummary = { subtotal: 0, total: 0, count: 0 };
      } finally {
        this.lineItemsLoading = false;
      }
    },
    async fetchRelatedPropertyRecords() {
      const companyId = Number(this.deal?.ncrm_company_id || 0) || null;
      const personId = Number(this.deal?.ncrm_person_id || 0) || null;
      if (!companyId && !personId) {
        this.companyRecord = null;
        this.personRecord = null;
        this.resetPropertyLayoutStateForTarget("company");
        this.resetPropertyLayoutStateForTarget("person");
        return;
      }
      this.relatedPropertiesLoading = true;
      try {
        const [companyResult, personResult] = await Promise.all([
          companyId
            ? axios
                .get(`/ncrm/companies/${companyId}`)
                .then((response) => response?.data || null)
                .catch((error) => {
                  console.error("[ncrm-deal-details] load company properties error", error);
                  return null;
                })
            : Promise.resolve(null),
          personId
            ? axios
                .get(`/ncrm/people/${personId}`)
                .then((response) => response?.data || null)
                .catch((error) => {
                  console.error("[ncrm-deal-details] load person properties error", error);
                  return null;
                })
            : Promise.resolve(null),
        ]);
        this.companyRecord = companyResult;
        this.personRecord = personResult;
        if (!companyId) {
          this.resetPropertyLayoutStateForTarget("company");
        } else {
          this.syncPropertyLayoutStateForTarget("company");
        }
        if (!personId) {
          this.resetPropertyLayoutStateForTarget("person");
        } else {
          this.syncPropertyLayoutStateForTarget("person");
        }
        await Promise.all([
          companyId ? this.fetchPropertyLayoutForTarget("company", { force: true, silent: true }) : Promise.resolve(),
          personId ? this.fetchPropertyLayoutForTarget("person", { force: true, silent: true }) : Promise.resolve(),
        ]);
      } finally {
        this.relatedPropertiesLoading = false;
      }
    },
    resetLineItemForm() {
      this.lineItemForm = {
        product_id: null,
        name: "",
        product_type: "digital_one_time",
        quantity: "1",
        unit_price: "0",
        discount_type: "none",
        discount_value: "0",
        currency: String(this.lineItemsCurrency || "MDL").toUpperCase(),
        billing_period: "",
        is_active_subscription: true,
        notes: "",
      };
    },
    openCreateLineItemDialog() {
      this.editingLineItemId = null;
      this.resetLineItemForm();
      this.productsCatalog = [];
      this.lineItemDialog = true;
    },
    openEditLineItemDialog(item) {
      this.editingLineItemId = Number(item?.id || 0) || null;
      this.lineItemForm = {
        product_id: Number(item?.product_id || 0) || null,
        name: item?.name_snapshot || "",
        product_type: item?.product_type_snapshot || "digital_one_time",
        quantity: String(item?.quantity ?? "1"),
        unit_price: String(item?.unit_price ?? "0"),
        discount_type: item?.discount_type || "none",
        discount_value: String(item?.discount_value ?? "0"),
        currency: String(item?.currency || "MDL").toUpperCase(),
        billing_period: item?.billing_period_snapshot || "",
        is_active_subscription: Number(item?.is_active_subscription) === 1,
        notes: item?.notes || "",
      };
      this.productsCatalog = [];
      this.seedCurrentLineItemProductOption();
      this.lineItemDialog = true;
    },
    handleLineItemProductChange(productId) {
      const normalizedProductId = Number(productId || 0) || null;
      const product = (this.productsCatalog || []).find((item) => Number(item?.id || 0) === normalizedProductId);
      if (!product) return;
      this.lineItemForm.product_type = product.product_type || this.lineItemForm.product_type;
      this.lineItemForm.name = product.name || this.lineItemForm.name;
      this.lineItemForm.unit_price = String(toNumber(product.default_unit_price, 0));
      this.lineItemForm.currency = String(product.default_currency || "MDL").toUpperCase();
      if (product.product_type === "subscription") {
        this.lineItemForm.billing_period = product.billing_period || this.lineItemForm.billing_period || "monthly";
        this.lineItemForm.is_active_subscription = true;
      }
    },
    ensureProductsCatalogLoaded() {
      if (this.productsLoading) return;
      if (Array.isArray(this.productsCatalog) && this.productsCatalog.length) return;
      this.fetchProductsCatalog().catch((error) => {
        console.error("[ncrm-deal-details] lazy load products catalog error", error);
      });
    },
    ensureEditProductOptionsLoaded() {
      if (this.editProductsLoading) return;
      if (Array.isArray(this.editProductOptions) && this.editProductOptions.length) return;
      this.fetchEditProductOptions("").catch((error) => {
        console.error("[ncrm-deal-details] lazy load edit products error", error);
      });
    },
    ensureOwnerOptionsLoaded() {
      if (this.ownerLoading) return;
      if (Array.isArray(this.ownerOptions) && this.ownerOptions.length) return;
      this.fetchOwners("").catch((error) => {
        console.error("[ncrm-deal-details] lazy load owners error", error);
      });
    },
    buildLineItemPayload() {
      const payload = {
        product_id: Number(this.lineItemForm.product_id || 0) || null,
        name: String(this.lineItemForm.name || "").trim(),
        product_type: String(this.lineItemForm.product_type || "digital_one_time").trim(),
        quantity: Number(this.lineItemForm.quantity),
        unit_price: Number(this.lineItemForm.unit_price),
        discount_type: String(this.lineItemForm.discount_type || "none").trim(),
        discount_value: Number(this.lineItemForm.discount_value || 0),
        currency: String(this.lineItemForm.currency || "MDL").trim().toUpperCase(),
        billing_period:
          this.lineItemForm.product_type === "subscription"
            ? String(this.lineItemForm.billing_period || "").trim() || null
            : null,
        is_active_subscription:
          this.lineItemForm.product_type === "subscription"
            ? this.lineItemForm.is_active_subscription === true
            : false,
        notes: String(this.lineItemForm.notes || "").trim() || null,
      };
      return payload;
    },
    async submitLineItem() {
      if (!this.dealId || !this.canSubmitLineItem) return;
      this.lineItemSaving = true;
      try {
        const payload = this.buildLineItemPayload();
        if (this.editingLineItemId) {
          await axios.patch(`/ncrm/deals/${this.dealId}/line-items/${this.editingLineItemId}`, payload);
          this.showSnackbar(this.$t("ncrm.deals.details.lineItems.messages.updated"), "success");
        } else {
          await axios.post(`/ncrm/deals/${this.dealId}/line-items`, payload);
          this.showSnackbar(this.$t("ncrm.deals.details.lineItems.messages.created"), "success");
        }
        this.lineItemDialog = false;
        await this.fetchLineItems();
      } catch (error) {
        console.error("[ncrm-deal-details] save line item error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.lineItems.messages.saveError"),
          "error"
        );
      } finally {
        this.lineItemSaving = false;
      }
    },
    openDeleteLineItemDialog(item) {
      this.selectedLineItemForDelete = item || null;
      this.lineItemDeleteDialog = true;
    },
    async confirmDeleteLineItem() {
      const lineItemId = Number(this.selectedLineItemForDelete?.id || 0) || null;
      if (!this.dealId || !lineItemId) return;
      this.lineItemDeleteLoading = true;
      try {
        await axios.delete(`/ncrm/deals/${this.dealId}/line-items/${lineItemId}`);
        this.showSnackbar(this.$t("ncrm.deals.details.lineItems.messages.deleted"), "success");
        this.lineItemDeleteDialog = false;
        this.selectedLineItemForDelete = null;
        await this.fetchLineItems();
      } catch (error) {
        console.error("[ncrm-deal-details] delete line item error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.lineItems.messages.deleteError"),
          "error"
        );
      } finally {
        this.lineItemDeleteLoading = false;
      }
    },
    async loadDeal() {
      if (!this.dealId) return;
      this.loading = true;
      try {
        const { data } = await axios.get(`/ncrm/deals/${this.dealId}`);
        this.deal = data || null;
        await Promise.all([
          this.fetchStages(),
          this.fetchOwners(),
          this.fetchLineItems(),
          this.fetchProductsCatalog(),
          this.fetchRelatedPropertyRecords(),
        ]);
      } catch (error) {
        console.error("[ncrm-deal-details] load error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.messages.loadError"),
          "error"
        );
        this.deal = null;
        this.companyRecord = null;
        this.personRecord = null;
        this.resetPropertyLayoutStateForTarget("company");
        this.resetPropertyLayoutStateForTarget("person");
        this.lineItems = [];
        this.lineItemsSummary = { subtotal: 0, total: 0, count: 0 };
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      const pipelineId = Number(this.deal?.pipeline_id || 0) || null;
      if (pipelineId) {
        this.$router.push({
          name: "NcrmDealBoard",
          params: { id: String(pipelineId) },
        });
        return;
      }
      this.$router.push({ name: "NcrmDealsHome" });
    },
    openSourceRoute(route) {
      if (!route) return;
      this.$router.push(route).catch(() => {});
    },
    openSourceCompany() {
      this.openSourceRoute(this.sourceCompanyRoute);
    },
    openSourcePerson() {
      this.openSourceRoute(this.sourcePersonRoute);
    },
    openSourceLead() {
      this.openSourceRoute(this.sourceLeadRoute);
    },
    openEditDialog() {
      if (!this.deal) return;
      const selectedProductIds = Array.from(
        (this.lineItems || [])
          .map((item) => buildEditDealSelectionToken(EDIT_DEAL_SELECTION_KINDS.LINE_ITEM, item?.id))
          .filter(Boolean)
      );
      this.editForm = {
        name: this.deal?.name || "",
        owner_user_id: Number(this.deal?.owner_user_id || 0) || null,
        stage_id: Number(this.deal?.stage_id || 0) || null,
        status: String(this.deal?.status || "open").toLowerCase(),
        product_ids: selectedProductIds,
        expected_close_date: normalizeDate(this.deal?.expected_close_date),
        ncrm_company_id: Number(this.deal?.ncrm_company_id || 0) || null,
        ncrm_person_id: Number(this.deal?.ncrm_person_id || 0) || null,
        notes: this.deal?.notes || "",
      };
      this.seedEditCompanyOptionFromDeal();
      this.seedEditPersonOptionFromDeal();
      this.seedEditProductOptionsFromLineItems();
      this.seedOwnerOptionFromDeal();
      this.editDialog = true;
      Promise.all([
        this.fetchOwners(""),
        this.fetchEditProductOptions(""),
        this.fetchEditCompanyOptions(""),
        this.fetchEditPersonOptions(""),
      ]).catch((error) => {
        console.error("[ncrm-deal-details] preload edit options error", error);
      });
    },
    maybeOpenWonHandoffFromEdit({
      nextStatus = this.editForm?.status,
      nextStageId = this.editForm?.stage_id,
    } = {}) {
      if (!this.editDialog || this.wonHandoffDialog || !this.deal) return;
      const companyId = Number(this.editForm?.ncrm_company_id || 0) || null;
      const personId = Number(this.editForm?.ncrm_person_id || 0) || null;
      if (!companyId && !personId) return;
      const currentStatus = String(this.deal?.status || "open").trim().toLowerCase();
      const currentStageId = Number(this.deal?.stage_id || 0) || null;
      const requestedStatus = String(nextStatus || "open").trim().toLowerCase();
      const requestedStageId = Number(nextStageId || 0) || null;
      const requestedStageType = this.resolveStageTypeById(requestedStageId);
      const statusTransitionsToWon = requestedStatus !== currentStatus && requestedStatus === "won";
      const stageTransitionsToWon =
        requestedStageId &&
        requestedStageId !== currentStageId &&
        requestedStageType === "won" &&
        currentStatus !== "won";
      if (!statusTransitionsToWon && !stageTransitionsToWon) return;
      if (
        requestedStageId &&
        !this.ensureActiveTaskBeforeStageChange({
          targetStageId: requestedStageId,
          closeEditDialog: false,
        })
      ) {
        return;
      }
      const selection = this.resolveEditProductSelection(this.editForm?.product_ids);
      const expectedValue = this.estimateEditDealExpectedValue({
        selectedLineItemIds: selection.selectedExistingLineItemIds,
        productIdsToAttach: selection.productIdsToAttach,
      });
      this.openWonHandoffDialogFromEdit({
        targetStageId: requestedStageType === "won" ? requestedStageId : null,
        dealPatch: {
          name: this.editForm.name,
          owner_user_id: this.editForm.owner_user_id,
          expected_value: expectedValue,
          expected_close_date: this.editForm.expected_close_date || null,
          ncrm_company_id: companyId || null,
          ncrm_person_id: personId || null,
          notes: this.editForm.notes || null,
        },
        selectedLineItemIds: selection.selectedExistingLineItemIds,
        productIdsToAttach: selection.productIdsToAttach,
      });
    },
    openWonHandoffDialogFromEdit({
      targetStageId = null,
      dealPatch = null,
      selectedLineItemIds = null,
      productIdsToAttach = [],
      reopenEditOnCancel = true,
    } = {}) {
      this.pendingWonHandoffRequest = {
        targetStageId: Number(targetStageId || 0) || null,
        dealPatch: dealPatch && typeof dealPatch === "object" && !Array.isArray(dealPatch) ? { ...dealPatch } : null,
        initialLineItemIds: Array.isArray(selectedLineItemIds) ? [...selectedLineItemIds] : null,
        initialProductIdsToAttach: Array.isArray(productIdsToAttach) ? [...productIdsToAttach] : [],
        reopenEditOnCancel: Boolean(reopenEditOnCancel),
      };
      this.editDialog = false;
      this.wonHandoffDialog = true;
    },
    handleWonHandoffCancelled() {
      const shouldReopenEdit = Boolean(this.pendingWonHandoffRequest?.reopenEditOnCancel);
      this.pendingWonHandoffRequest = null;
      if (shouldReopenEdit) {
        this.editDialog = true;
      }
    },
    async handleWonHandoffSuccess(payload = {}) {
      this.pendingWonHandoffRequest = null;
      const contractId = Number(payload?.response?.data?.contract?.id || 0);
      if (Number.isFinite(contractId) && contractId > 0) {
        this.$router.push({
          name: "NcrmContractDetails",
          params: { contractId: String(Math.trunc(contractId)) },
          query: {
            tab: "billing",
            billingSetup: "1",
          },
        });
        return;
      }
      this.showSnackbar(this.$t("ncrm.deals.details.messages.handoffSuccess"), "success");
      await this.loadDeal();
    },
    handleWonHandoffError(payload = {}) {
      this.showSnackbar(
        payload?.message || this.$t("ncrm.deals.board.messages.handoffError"),
        payload?.color || "error"
      );
    },
    async submitEdit() {
      if (!this.dealId || !this.deal) return;
      const companyId = Number(this.editForm.ncrm_company_id || 0) || null;
      const personId = Number(this.editForm.ncrm_person_id || 0) || null;
      const selection = this.resolveEditProductSelection(this.editForm?.product_ids);
      if (!companyId && !personId) {
        this.showSnackbar(this.$t("ncrm.deals.details.messages.contactRequired"), "warning");
        return;
      }
      const currentStatus = String(this.deal?.status || "open").trim().toLowerCase();
      const requestedStatus = String(this.editForm?.status || "open").trim().toLowerCase();
      const currentStageId = Number(this.deal?.stage_id || 0) || null;
      const requestedStageId = Number(this.editForm?.stage_id || 0) || null;
      const stageWillChange = Boolean(requestedStageId && requestedStageId !== currentStageId);
      const requestedStageType = this.resolveStageTypeById(requestedStageId);
      const statusTransitionsToWon = requestedStatus !== currentStatus && requestedStatus === "won";
      const stageTransitionsToWon =
        requestedStageId &&
        requestedStageId !== currentStageId &&
        requestedStageType === "won" &&
        currentStatus !== "won";
      const statusTransitionsToLost =
        requestedStatus !== currentStatus && requestedStatus === "lost";
      const stageTransitionsToLost =
        requestedStageId &&
        requestedStageId !== currentStageId &&
        requestedStageType === "lost" &&
        currentStatus !== "lost";
      const requiresWonHandoff = Boolean(statusTransitionsToWon || stageTransitionsToWon);
      const expectedValue = this.estimateEditDealExpectedValue({
        selectedLineItemIds: selection.selectedExistingLineItemIds,
        productIdsToAttach: selection.productIdsToAttach,
      });
      const selectedExistingLineItemIds = selection.selectedExistingLineItemIds;
      const productIdsToAttach = selection.productIdsToAttach;
      const dealPatch = {
        name: this.editForm.name,
        owner_user_id: this.editForm.owner_user_id,
        expected_value: expectedValue,
        expected_close_date: this.editForm.expected_close_date || null,
        ncrm_company_id: companyId || null,
        ncrm_person_id: personId || null,
        notes: this.editForm.notes || null,
      };
      if (
        stageWillChange &&
        !this.ensureActiveTaskBeforeStageChange({
          targetStageId: requestedStageId,
          closeEditDialog: true,
          pendingEditRequest: requiresWonHandoff || statusTransitionsToLost || stageTransitionsToLost
            ? null
            : {
                dealPatch,
                targetStatus: String(this.editForm.status || "").toLowerCase(),
                productIdsToAttach,
                reason: null,
              },
        })
      ) {
        return;
      }
      this.editLoading = true;
      try {
        if (requiresWonHandoff) {
          this.editLoading = false;
          this.openWonHandoffDialogFromEdit({
            targetStageId: requestedStageType === "won" ? requestedStageId : null,
            dealPatch,
            selectedLineItemIds: selectedExistingLineItemIds,
            productIdsToAttach,
          });
          return;
        }
        if (statusTransitionsToLost || stageTransitionsToLost) {
          const closeOpenActivities = this.resolveLostDealTaskClosureConfirmation({
            targetStageId: stageTransitionsToLost ? requestedStageId : null,
            targetStatus: "lost",
          });
          if (closeOpenActivities === null) {
            this.editLoading = false;
            return;
          }
          this.editLoading = false;
          this.openLostReasonDialogFromEdit({
            targetStageId: stageTransitionsToLost ? requestedStageId : null,
            targetStatus: "lost",
            dealPatch,
            productIdsToAttach,
            reopenEditOnCancel: true,
            closeOpenActivities,
          });
          return;
        }
        await this.applyDealEditChanges({
          dealPatch,
          targetStageId: stageWillChange ? requestedStageId : null,
          targetStatus: String(this.editForm.status || "").toLowerCase(),
          productIdsToAttach,
          reason: null,
        });
        this.showSnackbar(this.$t("ncrm.deals.details.messages.updated"), "success");
        this.editDialog = false;
        await this.loadDeal();
        await this.refreshDealActivityTimelineIfOpen();
      } catch (error) {
        console.error("[ncrm-deal-details] save error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.messages.updateError"),
          "error"
        );
      } finally {
        this.editLoading = false;
      }
    },
    async submitDelete() {
      if (!this.dealId || !this.canDeleteSubmit) return;
      this.deleteLoading = true;
      try {
        await axios.delete(`/ncrm/deals/${this.dealId}`, {
          data: {
            confirmText: this.deleteForm.confirmText,
            delete_reason: this.deleteForm.deleteReason,
          },
        });
        this.showSnackbar(this.$t("ncrm.deals.details.messages.deleted"), "success");
        this.deleteDialog = false;
        this.goBack();
      } catch (error) {
        console.error("[ncrm-deal-details] delete error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.details.messages.deleteError"),
          "error"
        );
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.ncrm-deal-details-page {
  padding-bottom: 24px;
}
.ncrm-deal-details__side-column {
  display: grid;
  gap: 12px;
}
.ncrm-deal-details__header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: max-content;
  max-width: 100%;
}
.ncrm-deal-details__header-actions-secondary {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 8px;
}
.ncrm-deal-details__header-actions > .v-btn {
  width: 100%;
}
.ncrm-record-header__kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ncrm-record-header__kpi {
  appearance: none;
  border: 1px solid var(--ncrm-color-border-strong);
  border-radius: var(--app-radius-base);
  padding: 8px 10px;
  min-width: 176px;
  background-color: var(--ncrm-color-surface-muted);
  color: inherit;
  font: inherit;
  text-align: left;
}
.ncrm-record-header__kpi--clickable {
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.ncrm-record-header__kpi--clickable:hover {
  background-color: var(--ncrm-color-hover-default);
  border-color: var(--ncrm-color-primary-border);
}
.ncrm-record-header__kpi--clickable:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
}
.ncrm-record-header__kpi--clickable:disabled {
  cursor: default;
  opacity: 0.8;
}
.ncrm-record-header__kpi-value {
  color: var(--ncrm-color-text-strong);
  font-weight: 600;
}
.ncrm-deal-details__kpi-menu-list {
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
}
.ncrm-details-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ncrm-details-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
  padding-bottom: 4px;
}
.ncrm-details-list__item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.ncrm-details-list__value {
  color: var(--ncrm-color-text-strong);
  white-space: pre-wrap;
}
.ncrm-main-workspace-tabs {
  border-bottom: 1px solid var(--ncrm-color-border-default);
}
.ncrm-main-workspace-window {
  padding-top: 14px;
}
.ncrm-deal-details__products-panel {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.ncrm-deal-details__products-loading {
  display: grid;
  gap: 16px;
}
.ncrm-deal-details__products-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}
.ncrm-deal-details__product-card {
  padding: 18px;
  border-radius: var(--app-radius-base);
}
.ncrm-deal-details__product-card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}
.ncrm-deal-details__product-card-title-block {
  min-width: 0;
}
.ncrm-deal-details__product-card-header-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}
.ncrm-deal-details__product-card-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  padding: 16px 0;
}
.ncrm-deal-details__product-card-metric,
.ncrm-deal-details__product-card-detail {
  min-width: 0;
}
.ncrm-deal-details__product-card-details {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding-top: 14px;
  border-top: 1px solid var(--ncrm-color-border-subtle);
}
.ncrm-deal-details__product-card-detail--full {
  grid-column: 1 / -1;
}
.ncrm-deal-details__product-card-notes {
  white-space: pre-wrap;
  color: var(--ncrm-color-text-strong);
}
@media (max-width: 960px) {
  .ncrm-deal-details__header-actions {
    align-items: stretch;
    width: 100%;
  }
  .ncrm-deal-details__header-actions-secondary {
    justify-content: stretch;
    flex-wrap: wrap;
  }
  .ncrm-deal-details__header-actions > .v-btn,
  .ncrm-deal-details__header-actions-secondary > .v-btn {
    flex: 1 1 auto;
  }
  .ncrm-deal-details__product-card-header {
    flex-direction: column;
  }
  .ncrm-deal-details__product-card-header-side {
    align-items: stretch;
  }
  .ncrm-deal-details__product-card-details {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
